import { Component, OnInit, OnDestroy, signal, computed, inject, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService, MenuItem, TreeNode } from 'primeng/api';
import { Observable, Subject, debounceTime, distinctUntilChanged, finalize, map, of, takeUntil } from 'rxjs';
import { TreeTableLazyLoadEvent } from 'primeng/treetable';
import { AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { TranslateService } from '@ngx-translate/core';
import { EnumValues, UserLite, EnumObjectExtended, BreadcrumbService } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';

import { DispatchInstructionService } from '../../../services/dispatch-instruction.service';
import { EnumService } from '../../../services/enum.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { BarcodeScannerService } from '../../../services/barcode-scanner.service';
import { DispatchInstructionProxyService } from '../../../proxies/inventory/dispatch-instruction.proxy.service';
import { WorkOrderProxyService } from '../../../proxies/order/work-order.proxy.service';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { ClientPurchaseOrderProxyService } from '../../../proxies/order/client-purchase-order.proxy.service';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { ProductRecipeProxyService } from '../../../proxies/item/product-recipe.proxy.service';
import { BatchProxyService } from '../../../proxies/production/batch.proxy.service';
import { LotItemProxyService } from '../../../proxies/inventory/lot-item.proxy.service';

import { Business, Address, BusinessEntityType, AddressType, Representative } from '../../../models/finance/business';
import {
  DispatchInstructionInsert,
  DispatchInstructionLine,
  DispatchInstructionStatus,
  DispatchInstructionType,
  ScannedRoll,
  FabricAndBalance,
  OtherDispatchInstructionInfo,
  FabricCustomization,
  FabricCustomizationSplit,
  ChemicalDispatchInstructionLine,
  ChemicalDispatchLotItemBatchGroup,
  ChemicalDispatchLotItemRow,
  AddChemicalLotItemCommand,
  FabricColorCompletion,
} from '../../../models/dispatch-instruction/dispatch-instruction';
import { ClientPurchaseOrder, ClientPurchaseOrderItem, ClientPurchaseOrderStatus } from '../../../models/order/client-purchase-order';
import { LotItem } from '../../../models/inventory/lot-item';
import { ItemMaster } from '../../../models/item/item-master';
import { DispatchEntryCommand } from '../../../models/dispatch-instruction/dispatch-entry-command';
import { DispatchInstructionShippingCommand } from '../../../models/dispatch-instruction/dispatch-instruction-shipping-command';
import { DispatchInstructionBillingCommand } from '../../../models/dispatch-instruction/dispatch-instruction-billing-command';
import { DispatchInstructionExtraToleranceCommand } from '../../../models/dispatch-instruction/dispatch-instruction-extra-tolerance-command';
import { DispatchInstruction } from '../../../models/dispatch-instruction/dispatch-instruction';
import { FabricBalance, FabricRollGroup, RollTreeNode, ScannedRollTreeNode } from '../../../models/dispatch-instruction/table-tree';
import { Fabric } from '../../../models/fabric/fabric';
import {
  AvailableRollsResponse,
  BGRADE_ROLLS_PAGE_SIZE,
  ProductionCardRollGroup,
} from '../../../models/dispatch-instruction/available-rolls';
import { WorkOrder, CustomerProductOrder, WorkOrderType } from '../../../models/order/work-order';
import {
  AVAILABLE_ROLLS_CONFIG,
  SCANNED_ROLLS_CONFIG,
  DispatchRollsTreeTableConfig,
} from '../../common/tables/dispatch-rolls-tree-table/dispatch-rolls-tree-table.models';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import {
  CrossPcRollRow,
  CustomizationRollEntry,
  DispatchDocumentType,
  DispatchScanMode,
  LotItemBatchGroup,
  RollQualityFilter,
  SplitRollsInfo,
  WorkOrderColorOption,
} from '../../../models/dispatch-instruction/dispatch-instruction-view';
import { ROLL_QUALITY_FILTER_OPTIONS } from '../../../models/dispatch-instruction/dispatch-instruction.constants';
import { RollQuality } from '../../../models/production/roll-search';
import { ProductionCard } from '../../../models/production/production-card';
import { UnitOfMeasure } from '../../../models/inventory/inventory-adjustment';
import { Roll } from '../../../models/production/roll';

@Component({
  selector: 'app-dispatch-instruction-details',
  templateUrl: './dispatch-instruction-details.component.html',
  styleUrls: ['./dispatch-instruction-details.component.scss'],
})
export class DispatchInstructionDetailsComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly enumService = inject(EnumService);
  private readonly userAuthService = inject(UserAuthenticationService);
  private readonly barcodeScannerService = inject(BarcodeScannerService);
  private readonly dispatchInstructionProxyService = inject(DispatchInstructionProxyService);
  private readonly workOrderProxyService = inject(WorkOrderProxyService);
  private readonly businessProxyService = inject(BusinessProxyService);
  private readonly clientPurchaseOrderProxyService = inject(ClientPurchaseOrderProxyService);
  private readonly itemMasterProxyService = inject(ItemMasterProxyService);
  private readonly productRecipeProxyService = inject(ProductRecipeProxyService);
  private readonly batchProxyService = inject(BatchProxyService);
  private readonly lotItemProxyService = inject(LotItemProxyService);
  private readonly translateService = inject(TranslateService);
  readonly dispatchInstructionService = inject(DispatchInstructionService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  isLoading = signal(0);
  isNewRecord = signal(false);
  scanInput = signal('');
  isDownloadingAll = signal(false);

  // 'nexus' reads the Nexus QR code, 'edr' the EDR barcode.
  scanMode = signal<DispatchScanMode>('nexus');

  readonly canViewPrices = AuthService.hasRole(UserGroup.DispatchInstructionPriceViewer);

  readonly preInvoiceDocumentItems = computed<MenuItem[]>(() =>
    this.canViewPrices
      ? [
          {
            label: this.translate('Dispatch_Doc_Commercial_Invoice_Pre', 'Commercial Invoice (Pre-Invoicing)'),
            icon: 'pi pi-file-pdf',
            command: () => this.downloadSingleDocument('commercial-invoice-pre'),
          },
          ...this.buildCommonDocumentMenuItems(),
        ]
      : [],
  );

  readonly finalDocumentItems = computed<MenuItem[]>(() =>
    this.canViewPrices
      ? [
          {
            label: this.translate('Dispatch_Doc_Commercial_Invoice', 'Commercial Invoice'),
            icon: 'pi pi-file-pdf',
            command: () => this.downloadSingleDocument('commercial-invoice'),
          },
          ...this.buildCommonDocumentMenuItems(),
        ]
      : [],
  );

  showFabricCustomizationsDialog = signal(false);
  editableFabricCustomizations = signal<FabricCustomization[]>([]);

  userLite: UserLite | null = null;

  selectedClient = signal<Business | null>(null);

  readonly shippingAddresses = computed<Address[]>(() => {
    const client = this.selectedClient();
    if (!client?.addresses) return [];
    return client.addresses.filter((addr) => addr.addressTypes?.includes(AddressType.Shipping)) ?? [];
  });

  existingClientAddresses = signal<Address[]>([]);
  selectedShipToAddress = signal<Address | null>(null);

  existingClientRepresentatives = signal<Representative[]>([]);
  selectedShippingToRepresentative = signal<Representative | null>(null);

  selectedDispatchType = signal<DispatchInstructionType>(DispatchInstructionType.Standard);

  selectedBGradeWorkOrder = signal<WorkOrder | null>(null);

  selectedGreigeOnlyWorkOrder = signal<WorkOrder | null>(null);

  readonly isSingleLineCreateType = computed(() => {
    const type = this.selectedDispatchType();
    return type === DispatchInstructionType.BGrade || type === DispatchInstructionType.GreigeOnly;
  });

  selectedShippingToClient = signal<Business | null>(null);
  shippingToClientInput: Business | string | null = null;
  isShippingDataReady = signal<boolean>(false);

  selectedBillingToClient = signal<Business | null>(null);
  billingToClientInput: Business | string | null = null;

  hasConsignee = signal<boolean>(false);
  selectedConsigneeClient = signal<Business | null>(null);
  consigneeClientInput: Business | string | null = null;
  consigneeClientAddresses = signal<Address[]>([]);
  selectedConsigneeAddress = signal<Address | null>(null);
  billingClientAddresses = signal<Address[]>([]);
  selectedBillToAddress = signal<Address | null>(null);

  initialForm = new FormGroup({
    client: new FormControl<Business | null>(null, [Validators.required]),
    shipToAddress: new FormControl<Address | null>(null, [Validators.required]),
  });

  selectedWorkOrder = signal<WorkOrder | null>(null);

  readonly clientPOOptions = computed<CustomerProductOrder[]>(() => {
    const workOrder = this.selectedWorkOrder();
    return workOrder?.customerProductOrders ?? [];
  });

  selectedClientPO = signal<CustomerProductOrder | null>(null);

  readonly colorOptions = computed<WorkOrderColorOption[]>(() => {
    const workOrder = this.selectedWorkOrder();
    const clientPO = this.selectedClientPO();
    if (!workOrder || !clientPO) return [];

    const colors: WorkOrderColorOption[] = [];
    const seenColors = new Set<string>();

    for (const fabric of workOrder.fabrics ?? []) {
      for (const garmetDetail of fabric.garmetDetails ?? []) {
        if (garmetDetail.productOrderNumber === clientPO.productOrderNumber && garmetDetail.colorReference) {
          const key = `${garmetDetail.colorReference}-${garmetDetail.productOrderNumber}`;
          if (!seenColors.has(key)) {
            seenColors.add(key);
            colors.push({
              colorReference: garmetDetail.colorReference,
              productOrderNumber: garmetDetail.productOrderNumber ?? '',
            });
          }
        }
      }
    }
    return colors;
  });

  statuses: EnumValues = [];
  statusEnumObject?: EnumObjectExtended;
  fabricFormValues: EnumValues = {};
  unitOfMeasures: EnumValues = {};

  status = new FormControl<DispatchInstructionStatus>(DispatchInstructionStatus.Draft);
  invoiceComment = new FormControl<string | null>(null);
  extraTolerancePercentage = new FormControl<number | null>(null);
  extraToleranceComment = new FormControl<string | null>(null);

  clientBaseOveragePercentage = signal<number | null>(null);

  shippingInfoForm = new FormGroup({
    voyageId: new FormControl<string | null>(null),
    booking: new FormControl<string | null>(null),
  });

  entryForm = new FormGroup({
    lineId: new FormControl<string | null>(null),
    workOrder: new FormControl<WorkOrder | null>(null, [Validators.required]),
    clientProductOrder: new FormControl<CustomerProductOrder | null>(null, [Validators.required]),
    colorReference: new FormControl<string>('', [Validators.required]),
  });

  availableRollsTree = signal<RollTreeNode[]>([]);
  selectedRollNodes: RollTreeNode[] = [];
  availableRollsSearchTerm = signal<string>('');

  // B-Grade only: the available rolls are paginated and searched on the API (thousands of rolls),
  // so the table runs in lazy mode. Standard and GreigeOnly keep loading their full pre-filtered
  // response and filtering it in memory.
  availableRollsPageSize = signal<number>(BGRADE_ROLLS_PAGE_SIZE);
  availableRollsFirst = signal<number>(0);
  availableRollsTotalRecords = signal<number>(0);
  isLoadingAvailableRolls = signal<boolean>(false);
  private readonly availableRollsSearch$ = new Subject<string>();
  // Paging and the debounced search can overlap; only the newest request is allowed to paint.
  private availableRollsRequestId = 0;

  selectedQualityFilter = signal<RollQualityFilter>('all');
  readonly qualityFilterOptions = ROLL_QUALITY_FILTER_OPTIONS.map((option) => ({
    value: option.value,
    label: this.translate(option.labelKey, option.fallback),
  }));

  priorityDate = new FormControl<Date | null>(null);
  priorityListForDate = signal<DispatchInstruction[]>([]);
  originalPriorityIndex = signal<number>(-1);
  isPriorityLoading = signal(false);
  showPriorityDialog = signal(false);

  readonly dispatchInstruction = computed(() => this.dispatchInstructionService.dispatchInstruction());
  readonly mode = computed(() => this.dispatchInstructionService.mode());
  readonly lines = computed(() => this.dispatchInstructionService.lines());
  readonly selectedLine = computed(() => this.dispatchInstructionService.selectedLine());
  readonly scanProgress = computed(() => this.dispatchInstructionService.scanProgress());
  readonly totalRollCount = computed(() => this.dispatchInstructionService.totalRollCount());
  readonly scannedRollCount = computed(() => this.dispatchInstructionService.scannedRollCount());

  readonly isAddMode = computed(() => this.mode() === 'add');
  readonly isScanMode = computed(() => this.mode() === 'scan');
  readonly isReadOnlyMode = computed(() => this.mode() === 'readonly');
  readonly canEdit = computed(() => this.dispatchInstructionService.canEdit());
  readonly canScan = computed(() => this.dispatchInstructionService.canScan());

  readonly availableRollsConfig: DispatchRollsTreeTableConfig = AVAILABLE_ROLLS_CONFIG;
  get scannedRollsConfig(): DispatchRollsTreeTableConfig {
    return { ...SCANNED_ROLLS_CONFIG, showActions: this.canEdit() };
  }

  // Arrow functions so the tree table child can call them without losing `this`.
  readonly getColorNameBound = (colorId: string): string => this.getColorName(colorId);
  readonly lbsToKgBound = (lbs: number): number => this.lbsToKg(lbs);
  readonly getFabricByCodeBound = (code: string): Fabric | null => this.getFabricByCode(code);

  readonly isDraft = computed(() => this.dispatchInstructionService.isDraft());
  readonly isInProgress = computed(() => this.dispatchInstructionService.isInProgress());
  readonly isCompleted = computed(() => this.dispatchInstructionService.isCompleted());

  readonly isBGradeOrGreigeOnly = computed(
    () =>
      this.dispatchInstruction()?.type === DispatchInstructionType.BGrade ||
      this.dispatchInstruction()?.type === DispatchInstructionType.GreigeOnly,
  );

  /**
   * True only for the B-Grade flow, whose available rolls are paginated and searched on the API.
   * (`isBGradeType` also covers GreigeOnly, which still filters client-side.)
   */
  readonly isBGradePaged = computed(() => this.dispatchInstruction()?.type === DispatchInstructionType.BGrade);

  private readonly loadedAvailableRollsCount = computed(() =>
    this.availableRollsTree().reduce((total, group) => total + (group.children?.length ?? 0), 0),
  );

  /**
   * The paginator is hidden when the loaded rolls already cover every match - either the result set
   * fits in one page, or the search matched a Production Card and the API returned all of its rolls
   * ignoring the page size.
   */
  readonly showAvailableRollsPaginator = computed(
    () => this.isBGradePaged() && this.loadedAvailableRollsCount() < this.availableRollsTotalRecords(),
  );

  readonly hasPriorityAndDate = computed(() => {
    const di = this.dispatchInstruction();
    return di?.dateAndPriorityKey?.date && di?.dateAndPriorityKey?.priority !== undefined;
  });

  readonly canEditPriority = computed(() => {
    const di = this.dispatchInstruction();
    return di && di.status !== DispatchInstructionStatus.Completed && di.status !== DispatchInstructionStatus.Cancelled;
  });

  readonly isEditingEntry = computed(() => this.dispatchInstructionService.isEditingEntry());
  readonly editingEntry = computed(() => this.dispatchInstructionService.editingEntry());
  readonly selectedEntryFabricCodes = computed(() => this.dispatchInstructionService.selectedEntryFabricCodes());
  readonly selectedEntryColorReference = computed(() => this.dispatchInstructionService.selectedEntryColorReference());
  readonly selectedEntryWorkOrderReference = computed(() => this.dispatchInstructionService.selectedEntryWorkOrderReference());

  // Whether available rolls exist for the selected entry (before quality/search filtering)
  readonly hasAvailableRolls = computed<boolean>(() => {
    const allRolls = this.availableRollsTree();

    if (this.isBGradeType()) {
      // B-Grade searches on the API: a search with no matches must still render the section,
      // otherwise the search box unmounts and the user cannot clear the term.
      if (this.isBGradePaged() && this.availableRollsSearchTerm().trim().length > 0) {
        return true;
      }
      return allRolls.length > 0;
    }

    const workOrderRef = this.selectedEntryWorkOrderReference();
    const colorRef = this.selectedEntryColorReference();
    if (!workOrderRef || !colorRef) return false;
    return allRolls.some((group) => group.data.workOrderReference === workOrderRef && group.data.colorReference === colorRef);
  });

  readonly filteredAvailableRollsTree = computed<RollTreeNode[]>(() => {
    const allRolls = this.availableRollsTree();
    const isBGrade = this.isBGradeType();
    const workOrderRef = this.selectedEntryWorkOrderReference();
    const colorRef = this.selectedEntryColorReference();
    const searchTerm = this.availableRollsSearchTerm().toLowerCase().trim();
    const qualityFilter = this.selectedQualityFilter();

    if (isBGrade) {
      if (!this.selectedLine()) return [];
    } else {
      if (!workOrderRef || !colorRef) return [];
    }

    let filtered = isBGrade
      ? allRolls
      : allRolls.filter((group) => group.data.workOrderReference === workOrderRef && group.data.colorReference === colorRef);

    if (qualityFilter !== 'all') {
      const qualityFiltered: RollTreeNode[] = [];
      for (const group of filtered) {
        const matchingChildren =
          group.children?.filter((child) => {
            if (qualityFilter === 'first') return child.data.rollQuality === RollQuality.First;
            return child.data.rollQuality !== RollQuality.First;
          }) ?? [];
        if (matchingChildren.length > 0) {
          qualityFiltered.push({ ...group, children: matchingChildren } as RollTreeNode);
        }
      }
      filtered = qualityFiltered;
    }

    // B-Grade is excluded: its search runs on the API over the whole dataset, so the loaded page is
    // already the result set - re-filtering it here would only hide rolls matched by Production Card.
    if (searchTerm && !this.isBGradePaged()) {
      const searchResults: RollTreeNode[] = [];

      for (const group of filtered) {
        const pcMatches = group.data.productionCardNumber?.toLowerCase().includes(searchTerm);

        const matchingChildren = group.children?.filter((child) => child.data.rollNumber?.toLowerCase().includes(searchTerm)) ?? [];

        if (pcMatches) {
          searchResults.push({ ...group, expanded: true } as RollTreeNode);
        } else if (matchingChildren.length > 0) {
          searchResults.push({ ...group, children: matchingChildren, expanded: true } as RollTreeNode);
        }
      }

      return searchResults;
    }

    return filtered;
  });

  readonly scannedRollsByFabric = computed<FabricRollGroup[]>(() => {
    const line = this.selectedLine();
    if (!line) return [];

    const pcInfoMap = new Map<string, Pick<ProductionCard, 'id' | 'productionCardNumber'>>();
    for (const pc of line.productionCards ?? []) {
      pcInfoMap.set(pc.id, { id: pc.id, productionCardNumber: pc.productionCardNumber });
    }

    // Structure: fabricCode → (pcRef → ScannedRoll[])
    const fabricMap = new Map<string, Map<string, ScannedRoll[]>>();

    for (const fabric of line.fabricsAndBalances) {
      const fabricCode = fabric.fabricCode;

      if (!fabricMap.has(fabricCode)) {
        fabricMap.set(fabricCode, new Map());
      }
      const pcMap = fabricMap.get(fabricCode)!;

      for (const scannedRoll of fabric.scannedRolls) {
        // For GreigeOnly rolls (no production card), group by knitting order instead
        const pcRef = scannedRoll.roll?.productionCardReference || scannedRoll.roll?.knittingOrderReference || 'unknown';

        if (!pcMap.has(pcRef)) {
          pcMap.set(pcRef, []);
        }
        pcMap.get(pcRef)!.push(scannedRoll);
      }
    }

    const fabricGroups: FabricRollGroup[] = [];

    for (const [fabricCode, pcMap] of fabricMap) {
      const fabricRolls: ScannedRoll[] = [];
      const pcNodes: ScannedRollTreeNode[] = [];

      for (const [pcRef, rolls] of pcMap) {
        fabricRolls.push(...rolls);

        let pcInfo = pcInfoMap.get(pcRef);
        if (!pcInfo) {
          const sampleRoll = rolls[0]?.roll;
          const koNumber = sampleRoll?.knittingOrderReference === pcRef ? sampleRoll?.knittingOrderNumber : null;
          pcInfo = {
            id: pcRef,
            productionCardNumber:
              koNumber ??
              (pcRef === 'unknown' ? this.translate('Dispatch_Unknown_Production_Card', 'Unknown') : pcRef.substring(0, 8) + '...'),
          };
        }

        const rollNodes: ScannedRollTreeNode[] = rolls.map((scannedRoll) => ({
          data: {
            type: 'roll' as const,
            rollNumber: scannedRoll.rollNumber,
            colorReference: scannedRoll.roll?.colorReference,
            weightInLbs: scannedRoll.roll?.finalWeight?.weightInLbs,
            weightInKg: this.lbsToKg(scannedRoll.roll?.finalWeight?.weightInLbs ?? 0),
            yards: scannedRoll.roll?.finalLengthInYards ?? 0,
            scannedAt: scannedRoll.scannedAt,
            scannedBy: scannedRoll.scannedBy,
            scannedRoll: scannedRoll,
          },
        }));

        const pcTotalYards = rollNodes.reduce((sum, r) => sum + (r.data.yards ?? 0), 0);

        pcNodes.push({
          data: {
            type: 'production-card' as const,
            productionCardId: pcInfo.id,
            productionCardNumber: pcInfo.productionCardNumber,
            rollCount: rolls.length,
            totalWeight: rolls.reduce((sum, r) => sum + (r.roll?.finalWeight?.weightInLbs ?? 0), 0),
            totalYards: pcTotalYards,
          },
          expanded: true,
          children: rollNodes,
        });
      }

      const fabricTotalYards = pcNodes.reduce((sum, pc) => sum + (pc.data.totalYards ?? 0), 0);

      fabricGroups.push({
        fabricCode: fabricCode,
        fabricDescription: this.getFabricDescription(fabricCode),
        rollCount: fabricRolls.length,
        totalWeight: fabricRolls.reduce((sum, r) => sum + (r.roll?.finalWeight?.weightInLbs ?? 0), 0),
        totalYards: fabricTotalYards,
        pcTree: pcNodes,
      });
    }

    return fabricGroups;
  });

  readonly fabricBalances = computed<FabricBalance[]>(() => {
    const lines = this.lines();
    const balanceMap = new Map<string, FabricBalance>();

    for (const line of lines) {
      const colorReference = line.colorReference;
      for (const fabric of line.fabricsAndBalances) {
        const key = `${fabric.fabricCode}|${colorReference}`;
        const existing = balanceMap.get(key);
        const weight = this.calculateFabricWeight(fabric);
        const rollCount = fabric.scannedRolls.length;
        const yards = fabric.scannedRolls.reduce((sum, sr) => sum + (sr.roll?.finalLengthInYards ?? 0), 0);

        if (existing) {
          existing.totalInDispatch += weight;
          existing.rollCount += rollCount;
          existing.totalYards += yards;
        } else {
          balanceMap.set(key, {
            fabricCode: fabric.fabricCode,
            colorReference: colorReference ?? '',
            totalInDispatch: weight,
            totalInFinishedGoods: 0, // Will be populated from available rolls
            rollCount: rollCount,
            totalYards: yards,
          });
        }
      }
    }

    return Array.from(balanceMap.values());
  });

  // #region Work Order Progress
  readonly isWorkOrderInYards = computed<boolean>(() => this.selectedLine()?.workOrder?.uom === UnitOfMeasure.yd);

  readonly workOrderUomLabel = computed<string>(() => (this.isWorkOrderInYards() ? 'yds' : 'lbs'));

  private readonly selectedLineGarmentTotals = computed<{
    quantity: number;
    quantityInPounds: number;
    ratioQuantity: number;
    ratioPounds: number;
  }>(() => {
    const line = this.selectedLine();
    const totals = { quantity: 0, quantityInPounds: 0, ratioQuantity: 0, ratioPounds: 0 };
    if (!line?.workOrder || !line.colorReference) return totals;

    for (const fabric of line.workOrder.fabrics ?? []) {
      for (const garment of fabric.garmetDetails ?? []) {
        if (garment.colorReference === line.colorReference && garment.productOrderNumber === line.clientProductOrderNumber) {
          totals.quantity += garment.quantity ?? 0;
          totals.quantityInPounds += garment.quantityInPounds ?? garment.quantity ?? 0;
          if ((garment.quantityInPounds ?? 0) > 0) {
            totals.ratioQuantity += garment.quantity ?? 0;
            totals.ratioPounds += garment.quantityInPounds!;
          }
        }
      }
    }

    return totals;
  });

  private readonly yardsPerPound = computed<number>(() => {
    const { ratioQuantity, ratioPounds } = this.selectedLineGarmentTotals();
    return ratioPounds > 0 ? ratioQuantity / ratioPounds : 0;
  });

  readonly selectLineWorkOrder = computed<number>(() => {
    const totals = this.selectedLineGarmentTotals();
    return this.isWorkOrderInYards() ? totals.quantity : totals.quantityInPounds;
  });

  private rollWeightInLbs(roll: Roll): number {
    return roll.finalWeight?.weightInLbs ?? roll.netQuantity ?? roll.plannedWeight ?? 0;
  }

  private isRollYardsEstimated(roll: Roll | undefined): boolean {
    return !!roll && !((roll.finalLengthInYards ?? 0) > 0);
  }

  private rollQuantityInUom(roll: Roll | undefined): number {
    if (!roll) return 0;
    if (!this.isWorkOrderInYards()) return this.rollWeightInLbs(roll);
    if (!this.isRollYardsEstimated(roll)) return roll.finalLengthInYards!;

    return this.rollWeightInLbs(roll) * this.yardsPerPound();
  }

  dispatchQuantityInUom(di: OtherDispatchInstructionInfo): number {
    return (di.rolls ?? []).reduce((sum, roll) => sum + this.rollQuantityInUom(roll), 0);
  }

  readonly hasEstimatedYards = computed<boolean>(() => {
    if (!this.isWorkOrderInYards()) return false;

    const line = this.selectedLine();
    const otherRolls = [...this.dispatchesCompleted(), ...this.dispatchesPending()].flatMap((di) => di.rolls ?? []);
    const currentRolls = (line?.fabricsAndBalances ?? []).flatMap((fabric) => fabric.scannedRolls.map((scanned) => scanned.roll));

    return [...otherRolls, ...currentRolls].some((roll) => this.isRollYardsEstimated(roll));
  });

  readonly isYardsEstimateUnavailable = computed<boolean>(() => this.hasEstimatedYards() && this.yardsPerPound() === 0);

  readonly dispatchesCompleted = computed<OtherDispatchInstructionInfo[]>(() => {
    const line = this.selectedLine();
    if (!line?.otherDispatchInstructionsWithRolls) return [];

    return line.otherDispatchInstructionsWithRolls.filter((di) => di.status === DispatchInstructionStatus.Completed);
  });

  readonly dispatchesPending = computed<OtherDispatchInstructionInfo[]>(() => {
    const line = this.selectedLine();
    if (!line?.otherDispatchInstructionsWithRolls) return [];

    return line.otherDispatchInstructionsWithRolls.filter((di) => di.status === DispatchInstructionStatus.InProgress);
  });

  readonly totalWeightCompleted = computed<number>(() => {
    return this.dispatchesCompleted().reduce((sum, di) => sum + this.dispatchQuantityInUom(di), 0);
  });

  readonly totalWeightPending = computed<number>(() => {
    return this.dispatchesPending().reduce((sum, di) => sum + this.dispatchQuantityInUom(di), 0);
  });
  readonly currentLineWeight = computed<number>(() => {
    const line = this.selectedLine();
    if (!line) return 0;

    let total = 0;
    for (const fabric of line.fabricsAndBalances) {
      for (const roll of fabric.scannedRolls) {
        total += this.rollQuantityInUom(roll.roll);
      }
    }
    return total;
  });

  readonly totalWeightDispatched = computed<number>(() => {
    return this.totalWeightCompleted() + this.totalWeightPending() + this.currentLineWeight();
  });

  readonly remainingWeight = computed<number>(() => {
    return Math.round((this.selectLineWorkOrder() - this.totalWeightDispatched()) * 100) / 100;
  });

  readonly excessWeight = computed<number>(() => {
    return Math.max(0, -this.remainingWeight());
  });

  readonly isOverDispatched = computed<boolean>(() => this.excessWeight() > 0);

  readonly workOrderProgressPercentage = computed<number>(() => {
    const required = this.selectLineWorkOrder();
    if (required === 0) return 0;

    return (this.totalWeightDispatched() / required) * 100;
  });

  readonly progressBarScale = computed<number>(() => {
    return Math.max(this.selectLineWorkOrder(), this.totalWeightDispatched());
  });

  readonly requiredMarkerPercentage = computed<number>(() => {
    const scale = this.progressBarScale();
    if (scale === 0) return 0;

    return (this.selectLineWorkOrder() / scale) * 100;
  });

  progressBarPercentage(weight: number): number {
    const scale = this.progressBarScale();
    if (scale === 0) return 0;

    return (weight / scale) * 100;
  }
  // #endregion

  readonly Math = Math;
  readonly DispatchInstructionType = DispatchInstructionType;

  readonly isBGradeType = computed(() => {
    const type = this.dispatchInstruction()?.type;
    return type === DispatchInstructionType.BGrade || type === DispatchInstructionType.GreigeOnly;
  });

  // #region Chemical dispatch (Pride Chemicals)

  readonly isChemicalType = computed(() => this.dispatchInstruction()?.type === DispatchInstructionType.Chemical);

  selectedChemicalClientPO = signal<ClientPurchaseOrder | null>(null);
  availableChemicalLotItems = signal<LotItem[]>([]);
  // The available-lots endpoint drops shipped lots, so a Completed dispatch's own lots are fetched by number.
  assignedChemicalLotItems = signal<LotItem[]>([]);
  selectedChemicalLineId = signal<string | null>(null);
  chemicalEntryItemCode = signal<string | null>(null);
  chemicalItemMastersByCode = signal<Map<string, ItemMaster>>(new Map());
  chemicalRecipeNamesByNumber = signal<Map<string, string>>(new Map());

  chemicalForm = new FormGroup({
    clientPurchaseOrder: new FormControl<ClientPurchaseOrder | null>(null, [Validators.required]),
    itemCode: new FormControl<string | null>(null, [Validators.required]),
  });

  readonly chemicalItemOptions = computed<ClientPurchaseOrderItem[]>(() => this.selectedChemicalClientPO()?.items ?? []);

  readonly chemicalLines = computed<ChemicalDispatchInstructionLine[]>(() => this.dispatchInstruction()?.chemicalLines ?? []);

  readonly selectedChemicalLine = computed<ChemicalDispatchInstructionLine | null>(() => {
    const lineId = this.selectedChemicalLineId();
    if (!lineId) return null;
    return this.chemicalLines().find((l) => l.lineId === lineId) ?? null;
  });

  readonly filteredChemicalLotItems = computed(() => {
    const line = this.selectedChemicalLine();
    if (!line) return [];
    return this.availableChemicalLotItems().filter((li) => li.itemCode === line.itemCode);
  });

  readonly groupedChemicalLotItems = computed(() => this.groupLotItemsByBatch(this.filteredChemicalLotItems()));

  readonly entryAvailableChemicalLotItems = computed<LotItem[]>(() => {
    const itemCode = this.chemicalEntryItemCode();
    if (!itemCode) return [];
    return this.availableChemicalLotItems().filter((li) => li.itemCode === itemCode);
  });

  readonly assignedChemicalLotItemNumbers = computed<Set<string>>(() => {
    const set = new Set<string>();
    for (const line of this.chemicalLines()) {
      for (const lotItem of line.lotItems ?? []) {
        set.add(lotItem.lotItemNumber);
      }
    }
    return set;
  });

  readonly chemicalLineSummaries = computed(() =>
    this.chemicalLines().map((line) => {
      const lotItems = line.lotItems ?? [];
      const totalQuantity = lotItems.reduce((sum, li) => sum + (li.quantity ?? 0), 0);
      return {
        line,
        lotItemCount: lotItems.length,
        totalQuantity,
        itemName: this.getChemicalItemName(line.itemCode),
        recipeNumber: this.getChemicalRecipeNumber(line.itemCode),
      };
    }),
  );

  readonly selectedLineLotItemsByBatch = computed<ChemicalDispatchLotItemBatchGroup[]>(() => {
    const line = this.selectedChemicalLine();
    if (!line) return [];
    const lookup = new Map<string, LotItem>();
    for (const li of [...this.availableChemicalLotItems(), ...this.assignedChemicalLotItems()]) {
      if (li.number) lookup.set(li.number, li);
    }
    const groups = new Map<string | null, ChemicalDispatchLotItemRow[]>();
    for (const assignment of line.lotItems ?? []) {
      const detail = lookup.get(assignment.lotItemNumber);
      const batchKey = detail?.batchNumber ?? null;
      const bucket = groups.get(batchKey) ?? [];
      bucket.push({
        lotItemNumber: assignment.lotItemNumber,
        quantity: assignment.quantity,
        locationCode: detail?.currentLocationCode,
        manufacturerLotNumber: detail?.providerLotId,
      });
      groups.set(batchKey, bucket);
    }
    return Array.from(groups.entries()).map(([batchNumber, items]) => ({
      batchNumber,
      items,
      totalQuantity: items.reduce((sum, i) => sum + (i.quantity ?? 0), 0),
    }));
  });

  // #endregion

  readonly availableStatuses = computed<EnumValues>(() => {
    const current = this.dispatchInstruction()?.status;

    if (current === undefined || current === null) {
      return Object.fromEntries(Object.entries(this.statuses).filter(([key]) => Number(key) === DispatchInstructionStatus.Draft));
    }

    let allowedStatuses: DispatchInstructionStatus[] = [];

    // Chemical dispatches (Pride Chemicals) skip the warehouse/scan workflow and commit
    // directly from Draft to Completed.
    if (this.dispatchInstruction()?.type === DispatchInstructionType.Chemical) {
      allowedStatuses =
        current === DispatchInstructionStatus.Draft
          ? [DispatchInstructionStatus.Draft, DispatchInstructionStatus.Completed, DispatchInstructionStatus.Cancelled]
          : [current];
      allowedStatuses = allowedStatuses.filter((s) => s === current || this.canTransitionTo(s));

      return Object.fromEntries(
        Object.entries(this.statuses).filter(([key]) => allowedStatuses.includes(Number(key) as DispatchInstructionStatus)),
      );
    }

    switch (current) {
      case DispatchInstructionStatus.Draft:
        allowedStatuses = [DispatchInstructionStatus.Draft, DispatchInstructionStatus.SentToWarehouse, DispatchInstructionStatus.Cancelled];
        break;
      case DispatchInstructionStatus.SentToWarehouse:
        allowedStatuses = [
          DispatchInstructionStatus.SentToWarehouse,
          DispatchInstructionStatus.InProgress,
          DispatchInstructionStatus.Cancelled,
        ];
        break;
      case DispatchInstructionStatus.InProgress:
        allowedStatuses = [DispatchInstructionStatus.InProgress, DispatchInstructionStatus.Completed, DispatchInstructionStatus.Cancelled];
        break;
      case DispatchInstructionStatus.Completed:
      case DispatchInstructionStatus.Cancelled:
        allowedStatuses = [current];
        break;
      default:
        allowedStatuses = [current];
    }

    allowedStatuses = allowedStatuses.filter((s) => s === current || this.canTransitionTo(s));

    return Object.fromEntries(
      Object.entries(this.statuses).filter(([key]) => allowedStatuses.includes(Number(key) as DispatchInstructionStatus)),
    );
  });

  /**
   * Role gate for Dispatch Instruction status transitions (PBI 4569). Roles are not mutually
   * exclusive — a user holding several can perform every transition each individually allows.
   * Mirrors DispatchInstructionService.ValidateStatusChangeRole on the backend.
   */
  private canTransitionTo(target: DispatchInstructionStatus): boolean {
    switch (target) {
      case DispatchInstructionStatus.SentToWarehouse:
      case DispatchInstructionStatus.Cancelled:
        return AuthService.hasRole(UserGroup.DispatchInstructionCreationAdmin);
      case DispatchInstructionStatus.InProgress:
        return AuthService.hasRole(UserGroup.DispatchInstructionWarehouseAdmin);
      case DispatchInstructionStatus.Completed:
        return AuthService.hasRole(UserGroup.DispatchInstructionCompleter);
      default:
        return true;
    }
  }

  ngOnInit(): void {
    this.userLite = this.userAuthService.userLite;
    this.loadEnums();
    this.initializeFromRoute();
    this.setupStatusListener();
    this.setupBarcodeScanner();
    this.setupAvailableRollsSearch();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.dispatchInstructionService.reset();
  }

  private increaseLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private decreaseLoading(): void {
    this.isLoading.update((v) => Math.max(0, v - 1));
  }
  private translate(key: string, fallback: string, params?: Record<string, string>): string {
    const translation = this.translateService.instant(key, params);
    if (translation !== key) {
      return translation;
    }
    if (params) {
      let result = fallback;
      for (const [paramKey, paramValue] of Object.entries(params)) {
        result = result.replace(new RegExp(`{{${paramKey}}}`, 'g'), paramValue);
      }
      return result;
    }
    return fallback;
  }

  private loadEnums(): void {
    this.statusEnumObject = this.enumService.getEnum('DispatchInstructionStatus', 'inventory');
    this.statuses = this.statusEnumObject?.values ?? {};
    this.fabricFormValues = this.enumService.getEnum('FabricForm', 'item').values;
    this.unitOfMeasures = this.enumService.getEnum('UnitOfMeasure', 'item').values;
  }

  private initializeFromRoute(): void {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = params['id'];
      const type = params['type'];

      if (id && id !== 'new') {
        this.isNewRecord.set(false);
        this.loadDispatchInstruction(id);
      } else {
        this.isNewRecord.set(true);
        this.breadcrumbService.setCurrent('Common_New');
        const dispatchType = type !== undefined ? Number(type) : DispatchInstructionType.Standard;
        this.selectedDispatchType.set(dispatchType as DispatchInstructionType);
      }
    });
  }

  private loadDispatchInstruction(id: string): void {
    this.increaseLoading();
    this.dispatchInstructionService
      .initialize(id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.decreaseLoading()),
      )
      .subscribe({
        next: (di) => {
          this.breadcrumbService.setCurrent(di.number);
          this.status.setValue(di.status);
          this.shippingInfoForm.patchValue({
            voyageId: di.voyageId ?? null,
            booking: di.booking ?? null,
          });
          this.invoiceComment.setValue(di.invoiceComment ?? null);
          this.extraTolerancePercentage.setValue(di.extraTolerancePercentage ?? null);
          this.extraToleranceComment.setValue(di.extraToleranceComment ?? null);
          this.hasConsignee.set(!!di.consigneeReference);
          // Freshly loaded values are not "unsaved changes"
          this.shippingInfoForm.markAsPristine();
          this.invoiceComment.markAsPristine();
          this.extraTolerancePercentage.markAsPristine();
          this.extraToleranceComment.markAsPristine();
          this.updateShippingFormDisabledState();
          this.loadAvailableRollsForLines();
          this.loadCachedData();
          this.loadClientAddresses();
          if (di.type === DispatchInstructionType.Chemical) {
            this.loadChemicalLotItems();
            this.loadChemicalItemMasters();
          }
        },
        error: () => {
          this.router.navigate(['/dispatch-instructions']);
        },
      });
  }

  private loadCachedData(): void {
    this.dispatchInstructionService.loadCachedData().pipe(takeUntil(this.destroy$)).subscribe();
  }

  private setupStatusListener(): void {
    this.status.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((newStatus) => {
      if (newStatus !== null && newStatus !== this.dispatchInstruction()?.status) {
        this.onStatusChange(newStatus);
      }
    });
  }

  private updateShippingFormDisabledState(): void {
    this.shippingInfoForm.enable({ emitEvent: false });
  }

  onSaveShippingInfo(): void {
    const di = this.dispatchInstruction();
    if (!di) return;

    if (!this.isShippingDataReady()) return;

    const formValue = this.shippingInfoForm.value;
    const shippingToClient = this.selectedShippingToClient();
    const shipToAddress = this.selectedShipToAddress();
    const shippingToRepresentative = this.selectedShippingToRepresentative();

    const command: DispatchInstructionShippingCommand = {
      shippingToReference: shippingToClient?.id,
      shipToAddressCode: shipToAddress?.code?.toString(),
      consigneeReference: di.consigneeReference,
      consigneeAddressCode: di.consigneeAddressCode,
      shippingToRepresentativeReference: shippingToRepresentative?.id,
      voyageId: formValue.voyageId ?? undefined,
      booking: formValue.booking ?? undefined,
    };

    this.increaseLoading();
    this.dispatchInstructionProxyService
      .updateShippingInfo(di.id, command)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.decreaseLoading()),
      )
      .subscribe({
        next: (response) => {
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            this.translate('Dispatch_Msg_Shipping_Updated', 'Shipping information updated successfully'),
          );
          if (response.data) {
            this.dispatchInstructionService.refreshDispatchInstruction(response.data);
            this.shippingInfoForm.patchValue(
              {
                voyageId: response.data.voyageId ?? null,
                booking: response.data.booking ?? null,
              },
              { emitEvent: false },
            );
          }
          this.shippingInfoForm.markAsPristine();
        },
        error: () => {
          this.shippingInfoForm.patchValue(
            {
              voyageId: di.voyageId ?? null,
              booking: di.booking ?? null,
            },
            { emitEvent: false },
          );
          this.shippingInfoForm.markAsPristine();
        },
      });
  }

  private setupBarcodeScanner(): void {
    this.barcodeScannerService.cleanBarcode$.pipe(takeUntil(this.destroy$)).subscribe((rollNumber) => {
      if (rollNumber && this.canScan()) {
        this.onScanRoll(rollNumber);
      }
    });
  }

  /** B-Grade only: debounce the search box and re-query the API from the first page. */
  private setupAvailableRollsSearch(): void {
    this.availableRollsSearch$.pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(() => {
      this.availableRollsFirst.set(0);
      this.loadBGradeAvailableRollsPage();
    });
  }

  onAvailableRollsSearchChange(searchTerm: string): void {
    this.availableRollsSearchTerm.set(searchTerm);

    // Standard and GreigeOnly filter the already loaded rolls through filteredAvailableRollsTree().
    if (this.isBGradePaged()) {
      this.availableRollsSearch$.next(searchTerm);
    }
  }

  onAvailableRollsPage(event: TreeTableLazyLoadEvent): void {
    const first = event.first ?? 0;
    const rows = event.rows ?? this.availableRollsPageSize();
    // The paginator also fires this when only the rows-per-page changes, so both have to be compared.
    if (first === this.availableRollsFirst() && rows === this.availableRollsPageSize()) return;

    this.availableRollsFirst.set(first);
    this.availableRollsPageSize.set(rows);
    this.loadBGradeAvailableRollsPage();
  }

  private loadAvailableRollsForLines(): void {
    const workOrderRefs = [
      ...new Set(
        this.lines()
          .map((line) => line.workOrderReference)
          .filter((ref): ref is string => !!ref),
      ),
    ];

    if (workOrderRefs.length === 0) return;

    const type = this.dispatchInstruction()?.type;
    if (type === DispatchInstructionType.BGrade) {
      this.loadBGradeAvailableRollsPage();
      return;
    }

    let load$: Observable<AvailableRollsResponse>;
    if (type === DispatchInstructionType.GreigeOnly) {
      load$ = this.dispatchInstructionService.loadGreigeOnlyAvailableRolls(workOrderRefs);
    } else {
      load$ = this.dispatchInstructionService.loadAvailableRolls(workOrderRefs);
    }

    load$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.buildAvailableRollsTree(Object.values(response.productionCardGroups));
      },
    });
  }

  private loadBGradeAvailableRollsPage(): void {
    const pageSize = this.availableRollsPageSize();
    const pageNumber = Math.floor(this.availableRollsFirst() / pageSize) + 1;
    const searchTerm = this.availableRollsSearchTerm().trim();
    const requestId = ++this.availableRollsRequestId;

    this.isLoadingAvailableRolls.set(true);
    this.dispatchInstructionService
      .loadBGradeAvailableRolls(pageNumber, pageSize, searchTerm || undefined)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          if (requestId === this.availableRollsRequestId) this.isLoadingAvailableRolls.set(false);
        }),
      )
      .subscribe({
        next: (page) => {
          if (requestId !== this.availableRollsRequestId) return;

          this.availableRollsTotalRecords.set(page.totalCount);

          // Rolls may have been dispatched since the page was opened, leaving the current page out
          // of range: fall back to the first page instead of showing an empty table.
          if (this.availableRollsFirst() > 0 && this.availableRollsFirst() >= page.totalCount) {
            this.availableRollsFirst.set(0);
            this.loadBGradeAvailableRollsPage();
            return;
          }

          // Expanded on purpose: the API pages by roll but the tree's rows are Production Cards, and a
          // single card can hold hundreds of B-Grade rolls. Collapsed, a full page of 100 rolls can
          // render as one row - and consecutive pages inside the same card look identical.
          this.buildAvailableRollsTree(page.productionCardGroups, true);
        },
      });
  }

  private buildAvailableRollsTree(groups: ProductionCardRollGroup[], expandGroups = false): void {
    const treeNodes: RollTreeNode[] = groups.map((group) => ({
      data: {
        type: 'production-card',
        label: group.productionCardNumber,
        productionCardId: group.productionCardId,
        productionCardNumber: group.productionCardNumber,
        workOrderReference: group.workOrderReference,
        colorReference: group.colorReference,
      },
      children: group.rolls.map((roll) => {
        return {
          data: {
            type: 'roll',
            label: roll.number,
            rollNumber: roll.number,
            fabricCode: roll.expectedFabricCode ?? 'unknown',
            colorReference: roll.colorReference ?? group.colorReference,
            finalWeight: roll.finalWeight?.weightInLbs,
            yards: roll.finalLengthInYards ?? 0,
            rollQuality: roll.rollQuality,
            selected: false,
          },
        };
      }),
      expanded: expandGroups,
    }));

    this.availableRollsTree.set(treeNodes);
  }

  onStatusChange(newStatus: DispatchInstructionStatus): void {
    const currentStatus = this.dispatchInstruction()?.status ?? DispatchInstructionStatus.Draft;
    // Only chemical dispatches draw their invoice number on completion; fabric ones still draw it on download.
    const assignsInvoiceNumber =
      newStatus === DispatchInstructionStatus.Completed && this.isChemicalType() && !this.dispatchInstruction()?.invoiceNumber;

    this.confirmationService.confirm({
      key: 'main',
      message: assignsInvoiceNumber
        ? this.translate(
            'Dispatch_Confirm_Complete_Assigns_Invoice',
            'Completing this dispatch will assign its invoice number from the active CAI. This cannot be undone. Do you want to continue?',
          )
        : this.translate('Dispatch_Confirm_Status_Change', 'Are you sure you want to change the status?'),
      header: this.translate('Dispatch_Confirm_Status_Change_Header', 'Confirm Status Change'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.increaseLoading();
        this.dispatchInstructionService
          .updateStatus(newStatus)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => this.decreaseLoading()),
          )
          .subscribe({
            next: () => {
              GlobalMessageUtil.success(
                this.translate('Common_Success', 'Success'),
                this.translate('Dispatch_Msg_Status_Updated', 'Status updated successfully'),
              );
              this.updateShippingFormDisabledState();
              if (this.isChemicalType()) {
                this.loadChemicalLotItems();
              }
            },
            error: () => {
              this.status.setValue(currentStatus, { emitEvent: false });
            },
          });
      },
      reject: () => {
        this.status.setValue(currentStatus, { emitEvent: false });
      },
    });
  }

  /**
   * Client, ship-to, consignee and bill-to all search the same endpoint, and each field gets its own
   * directive instance, so one fetch covers the four of them. The old 2-character guards moved to the
   * templates' [searchOptions]; this field never had one, and still does not.
   */
  public fetchClients = (query: string): Observable<Business[]> =>
    this.businessProxyService
      .search({ name: query, businessTypes: [BusinessEntityType.Client] }, 1, 50)
      .pipe(map((response) => response.data?.items ?? []));

  onClientSelect(client: Business): void {
    this.selectedClient.set(client);
    this.initialForm.patchValue({ shipToAddress: null });
  }

  onClientClear(): void {
    this.selectedClient.set(null);
    this.initialForm.patchValue({ shipToAddress: null });
  }

  onSaveInitial(): void {
    if (!this.initialForm.valid) {
      this.initialForm.markAllAsTouched();
      return;
    }

    const type = this.selectedDispatchType();

    if (type === DispatchInstructionType.BGrade && !this.selectedBGradeWorkOrder()) {
      GlobalMessageUtil.warn(
        this.translate('Common_Warning', 'Warning'),
        this.translate('Dispatch_Msg_BGrade_WorkOrder_Required', 'Select a work order for the B-Grade dispatch.'),
      );
      return;
    }
    if (type === DispatchInstructionType.GreigeOnly && !this.selectedGreigeOnlyWorkOrder()) {
      GlobalMessageUtil.warn(
        this.translate('Common_Warning', 'Warning'),
        this.translate('Dispatch_Msg_GreigeOnly_WorkOrder_Required', 'Select a work order for the Greige Only dispatch.'),
      );
      return;
    }

    const client = this.initialForm.value.client;
    const shipToAddress = this.initialForm.value.shipToAddress;

    const newDispatch: DispatchInstructionInsert = {
      type: type,
      bGradeWorkOrderReference: type === DispatchInstructionType.BGrade ? this.selectedBGradeWorkOrder()?.id : undefined,
      greigeOnlyWorkOrderReference: type === DispatchInstructionType.GreigeOnly ? this.selectedGreigeOnlyWorkOrder()?.id : undefined,
      status: DispatchInstructionStatus.Draft,
      clientReference: client?.id ?? '',
      shipToAddressCode: shipToAddress?.code?.toString() ?? '',
      notes: [],
      lines: [],
    };

    this.increaseLoading();

    this.dispatchInstructionService
      .createNew(newDispatch)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.decreaseLoading()),
      )
      .subscribe({
        next: (created) => {
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            this.translate('Dispatch_Msg_Created', 'Dispatch instruction created successfully'),
          );
          this.isNewRecord.set(false);
          // Navigate to the same page with the new ID - this will reload and show entries section
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { id: created.id },
            replaceUrl: true,
          });
        },
      });
  }

  private searchWorkOrderByType(query: string, orderType: WorkOrderType): Observable<WorkOrder[]> {
    return this.workOrderProxyService.search({ orderNumber: query, orderType }).pipe(map((response) => response.data?.items ?? []));
  }

  public fetchBGradeWorkOrders = (query: string): Observable<WorkOrder[]> => this.searchWorkOrderByType(query, WorkOrderType.BGrade);

  onBGradeWorkOrderSelect(workOrder: WorkOrder): void {
    this.selectedBGradeWorkOrder.set(workOrder);
    this.autoSelectClientFromWorkOrder(workOrder);
  }

  onBGradeWorkOrderClear(): void {
    this.selectedBGradeWorkOrder.set(null);
    this.clearAutoSelectedClient();
  }

  public fetchGreigeOnlyWorkOrders = (query: string): Observable<WorkOrder[]> =>
    this.searchWorkOrderByType(query, WorkOrderType.GreigeOnly);

  onGreigeOnlyWorkOrderSelect(workOrder: WorkOrder): void {
    this.selectedGreigeOnlyWorkOrder.set(workOrder);
    this.autoSelectClientFromWorkOrder(workOrder);
  }

  onGreigeOnlyWorkOrderClear(): void {
    this.selectedGreigeOnlyWorkOrder.set(null);
    this.clearAutoSelectedClient();
  }

  private autoSelectClientFromWorkOrder(workOrder: WorkOrder): void {
    if (!workOrder.customerReference) return;

    this.businessProxyService
      .getById(workOrder.customerReference)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.data) {
            const client = response.data;
            this.selectedClient.set(client);
            this.initialForm.patchValue({ client });

            const shippingAddrs = client.addresses?.filter((addr) => addr.addressTypes?.includes(AddressType.Shipping)) ?? [];
            if (shippingAddrs.length === 1) {
              this.initialForm.patchValue({ shipToAddress: shippingAddrs[0] });
            } else {
              this.initialForm.patchValue({ shipToAddress: null });
            }
          }
        },
      });
  }

  private clearAutoSelectedClient(): void {
    this.selectedClient.set(null);
    this.initialForm.patchValue({ client: null, shipToAddress: null });
  }

  public fetchWorkOrders = (query: string): Observable<WorkOrder[]> => {
    const clientRef = this.dispatchInstruction()?.clientReference;

    return this.workOrderProxyService
      .search({ orderNumber: query, customerReference: clientRef }, 1, 25, 'orderNumber', 1)
      .pipe(map(({ data }) => data?.items ?? []));
  };

  onWorkOrderSelect(workOrder: WorkOrder): void {
    this.selectedWorkOrder.set(workOrder);
    this.selectedClientPO.set(null);
    this.entryForm.patchValue({
      clientProductOrder: null,
      colorReference: '',
    });
    this.dispatchInstructionService.loadWorkOrderColors([workOrder]).pipe(takeUntil(this.destroy$)).subscribe();
  }

  onWorkOrderClear(): void {
    this.selectedWorkOrder.set(null);
    this.selectedClientPO.set(null);
    this.entryForm.patchValue({
      clientProductOrder: null,
      colorReference: '',
    });
  }

  onClientPOSelect(clientPO: CustomerProductOrder): void {
    this.selectedClientPO.set(clientPO);
    this.entryForm.patchValue({
      colorReference: '',
    });
  }

  /**
   * Called when a row is selected in the table (clicking on the row, not the edit button)
   * This selects the line to show available rolls, WITHOUT entering edit mode
   */
  onRowSelectForRolls(line: DispatchInstructionLine): void {
    this.dispatchInstructionService.stopEditingEntry(false);

    this.dispatchInstructionService.selectLine(line.lineId);

    this.entryForm.reset();
    this.selectedWorkOrder.set(null);
    this.selectedClientPO.set(null);
  }

  onEditEntry(line: DispatchInstructionLine): void {
    this.dispatchInstructionService.startEditingEntry(line);

    this.entryForm.patchValue({
      lineId: line.lineId,
      colorReference: line.colorReference,
    });

    if (line.workOrder) {
      this.selectedWorkOrder.set(line.workOrder);
      this.entryForm.patchValue({ workOrder: line.workOrder });

      const matchingPO = line.workOrder.customerProductOrders?.find((po) => po.productOrderNumber === line.clientProductOrderNumber);
      if (matchingPO) {
        this.selectedClientPO.set(matchingPO);
        this.entryForm.patchValue({ clientProductOrder: matchingPO });
      }
    }
  }

  onAddEntry(): void {
    this.dispatchInstructionService.clearEntryForm();

    this.entryForm.reset();
    this.selectedWorkOrder.set(null);
    this.selectedClientPO.set(null);
  }

  onSaveEntry(): void {
    if (!this.entryForm.valid) {
      this.entryForm.markAllAsTouched();
      return;
    }

    const lineId = this.entryForm.value.lineId;
    const workOrder = this.entryForm.value.workOrder;
    const clientPO = this.entryForm.value.clientProductOrder;
    const colorReference = this.entryForm.value.colorReference;
    const isEditing = this.isEditingEntry();

    let command: DispatchEntryCommand;

    if (isEditing && lineId) {
      // When editing, only update client product order (work order and color are locked)
      const editingEntry = this.editingEntry();
      command = {
        lineId: lineId,
        workOrderNumber: editingEntry?.workOrderNumber ?? '',
        workOrderReference: editingEntry?.workOrderReference ?? '',
        clientProductOrderNumber: clientPO?.productOrderNumber ?? '',
        colorReference: editingEntry?.colorReference ?? '',
      };
    } else {
      // When inserting new entry, all fields are required
      command = {
        lineId: lineId ?? undefined,
        workOrderNumber: workOrder?.orderNumber ?? '',
        workOrderReference: workOrder?.id ?? '',
        clientProductOrderNumber: clientPO?.productOrderNumber ?? '',
        colorReference: colorReference ?? '',
      };
    }

    this.increaseLoading();
    this.dispatchInstructionService
      .upsertEntry(command)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.decreaseLoading()),
      )
      .subscribe({
        next: (updatedInstruction) => {
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            isEditing
              ? this.translate('Dispatch_Msg_Entry_Updated', 'Entry updated successfully')
              : this.translate('Dispatch_Msg_Entry_Added', 'Entry added successfully'),
          );

          // If adding new entry, keep it selected (without editing mode) so user can add rolls
          // If editing existing entry, clear selection
          if (!isEditing) {
            const createdLine = updatedInstruction.lines.find(
              (l) =>
                l.workOrderReference === command.workOrderReference &&
                l.colorReference === command.colorReference &&
                l.clientProductOrderNumber === command.clientProductOrderNumber,
            );
            if (createdLine) {
              this.dispatchInstructionService.stopEditingEntry(false);
              this.dispatchInstructionService.selectLine(createdLine.lineId);
            } else {
              this.dispatchInstructionService.stopEditingEntry(true);
            }
          } else {
            this.dispatchInstructionService.stopEditingEntry(true);
          }

          this.entryForm.reset();
          this.selectedWorkOrder.set(null);
          this.selectedClientPO.set(null);
          this.loadAvailableRollsForLines();
          this.loadCachedData();
        },
      });
  }

  onRemoveEntry(lineId: string): void {
    this.confirmationService.confirm({
      key: 'main',
      message: this.translate('Dispatch_Confirm_Remove_Entry', 'Are you sure you want to remove this entry?'),
      header: this.translate('Dispatch_Confirm_Delete_Header', 'Confirm Delete'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.increaseLoading();
        this.dispatchInstructionService
          .removeEntry(lineId)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => this.decreaseLoading()),
          )
          .subscribe({
            next: () => {
              GlobalMessageUtil.success(
                this.translate('Common_Success', 'Success'),
                this.translate('Dispatch_Msg_Entry_Removed', 'Entry removed successfully'),
              );
            },
          });
      },
    });
  }

  onAddSelectedRolls(): void {
    const selectedLine = this.selectedLine();
    if (!selectedLine) {
      GlobalMessageUtil.warn(
        this.translate('Common_Warning', 'Warning'),
        this.translate('Dispatch_Msg_Select_Entry', 'Please select an entry first'),
      );
      return;
    }

    // Selection survives paging (nodes from other pages stay in the array), so de-duplicate in case
    // the same roll was picked twice across page loads.
    const selectedRolls = [
      ...new Set(
        this.selectedRollNodes
          .filter((node) => node.data.type === 'roll')
          .map((node) => node.data.rollNumber!)
          .filter((rn) => !!rn),
      ),
    ];

    if (selectedRolls.length === 0) {
      GlobalMessageUtil.warn(
        this.translate('Common_Warning', 'Warning'),
        this.translate('Dispatch_Msg_Select_Roll', 'Please select at least one roll'),
      );
      return;
    }

    this.increaseLoading();
    this.dispatchInstructionService
      .addRollsToEntry(selectedLine.lineId, selectedRolls)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.decreaseLoading()),
      )
      .subscribe({
        next: () => {
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            `${selectedRolls.length} ${this.translate('Dispatch_Msg_Rolls_Added', 'roll(s) added successfully')}`,
          );
          this.selectedRollNodes = [];
          this.loadAvailableRollsForLines();
        },
      });
  }

  onRemoveRoll(lineId: string, rollNumber: string): void {
    this.increaseLoading();
    this.dispatchInstructionService
      .removeRollsFromEntry(lineId, [rollNumber])
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.decreaseLoading()),
      )
      .subscribe({
        next: () => {
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            this.translate('Dispatch_Msg_Roll_Removed', 'Roll removed successfully'),
          );
          this.loadAvailableRollsForLines();
        },
      });
  }

  // Multi-select of already-scanned rolls, keyed per fabric group (checkbox mode on the "Rolls in Dispatch" table)
  selectedScannedRollNodes = new Map<string, ScannedRollTreeNode[]>();

  getSelectedScannedRollNodes(fabricCode: string): ScannedRollTreeNode[] {
    const stored = this.selectedScannedRollNodes.get(fabricCode);
    if (!stored || stored.length === 0) return [];

    // Production-card (parent) nodes have no rollNumber, so they're validated separately by
    // productionCardId — otherwise checking a PC's checkbox (which selects the PC node plus all
    // its children) would immediately strip the PC node out and break the parent checkbox toggle.
    const fabricGroup = this.scannedRollsByFabric().find((g) => g.fabricCode === fabricCode);
    const pcNodes = fabricGroup?.pcTree ?? [];
    const validRollNumbers = new Set(
      pcNodes.flatMap((pc) => (pc.children as ScannedRollTreeNode[] | undefined) ?? []).map((node) => node.data.rollNumber),
    );
    const validProductionCardIds = new Set(pcNodes.map((pc) => pc.data.productionCardId));

    return stored.filter((node) =>
      node.data.type === 'roll'
        ? !!node.data.rollNumber && validRollNumbers.has(node.data.rollNumber)
        : !!node.data.productionCardId && validProductionCardIds.has(node.data.productionCardId),
    );
  }

  onScannedRollSelectionChange(fabricCode: string, selection: TreeNode[]): void {
    this.selectedScannedRollNodes.set(fabricCode, (selection as ScannedRollTreeNode[]) ?? []);
  }

  getSelectedScannedRollNumbers(fabricCode: string): string[] {
    return this.getSelectedScannedRollNodes(fabricCode)
      .filter((node) => node.data.type === 'roll')
      .map((node) => node.data.rollNumber)
      .filter((rollNumber): rollNumber is string => !!rollNumber);
  }

  getSelectedScannedRollsCount(fabricCode: string): number {
    return this.getSelectedScannedRollNumbers(fabricCode).length;
  }

  onDeleteSelectedScannedRolls(lineId: string, fabricCode: string): void {
    const rollNumbers = this.getSelectedScannedRollNumbers(fabricCode);
    if (rollNumbers.length === 0) {
      return;
    }

    this.increaseLoading();
    this.dispatchInstructionService
      .removeRollsFromEntry(lineId, rollNumbers)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.decreaseLoading()),
      )
      .subscribe({
        next: () => {
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            `${rollNumbers.length} ${this.translate('Dispatch_Msg_Rolls_Removed', 'roll(s) removed successfully')}`,
          );
          this.selectedScannedRollNodes.delete(fabricCode);
          this.loadAvailableRollsForLines();
        },
      });
  }

  onScanRoll(scannedValue: string): void {
    if (!this.canScan()) return;

    let rollNumber = scannedValue;
    let isEdrScan = false;

    if (this.scanMode() === 'edr') {
      const foundRoll = this.findRollByYarnLotId(scannedValue);
      if (!foundRoll) {
        GlobalMessageUtil.warn(
          this.translate('Common_Warning', 'Warning'),
          this.translate('Dispatch_Msg_EDR_Not_Found', 'EDR barcode not found in any roll'),
        );
        this.scanInput.set('');
        return;
      }
      rollNumber = foundRoll.rollNumber;
      isEdrScan = true;
    }

    this.increaseLoading();
    this.dispatchInstructionService
      .scanRoll(rollNumber)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.decreaseLoading()),
      )
      .subscribe({
        next: () => {
          if (isEdrScan) {
            GlobalMessageUtil.success(
              this.translate('Common_Scanned', 'Scanned'),
              `${this.translate('Dispatch_Roll', 'Roll')} ${rollNumber} ${this.translate('Dispatch_Msg_Roll_Scanned', 'scanned successfully')}`,
              10000,
            );
          } else {
            GlobalMessageUtil.success(
              this.translate('Common_Scanned', 'Scanned'),
              `${this.translate('Dispatch_Roll', 'Roll')} ${rollNumber} ${this.translate('Dispatch_Msg_Roll_Scanned', 'scanned successfully')}`,
            );
          }
          this.scanInput.set('');
        },
        error: () => {
          this.scanInput.set('');
        },
      });
  }

  /** EDR barcodes carry the yarn lot id, not the roll number. */
  private findRollByYarnLotId(scannedValue: string): ScannedRoll | null {
    const allRolls = this.dispatchInstructionService.allScannedRolls();
    for (const scannedRoll of allRolls) {
      if (scannedRoll.roll?.yarnLotId?.includes(scannedValue)) {
        return scannedRoll;
      }
    }
    return null;
  }

  onManualScan(): void {
    const input = this.scanInput();
    if (input) {
      this.onScanRoll(input);
    }
  }

  private calculateFabricWeight(fabric: FabricAndBalance): number {
    return fabric.scannedRolls.reduce((sum, roll) => sum + (roll.roll?.finalWeight?.weightInLbs ?? 0), 0);
  }

  lbsToKg(lbs: number): number {
    return this.dispatchInstructionService.lbsToKg(lbs);
  }

  onCancel(): void {
    this.router.navigate(['/dispatch-instructions']);
  }

  getTotalWeight(): number {
    return this.fabricBalances().reduce((sum, balance) => sum + balance.totalInDispatch, 0);
  }

  getTotalYards(): number {
    return this.fabricBalances().reduce((sum, balance) => sum + balance.totalYards, 0);
  }

  // Completion % for a Fabric + Color, calculated by the API (2 exact decimals, not rounded)
  getFabricColorCompletion(fabricCode: string, colorReference: string): FabricColorCompletion | undefined {
    return this.dispatchInstruction()?.fabricColorCompletions?.find(
      (c) => c.fabricCode === fabricCode && c.colorReference === colorReference,
    );
  }

  // The selection also holds the production-card parents; only roll nodes count.
  getSelectedRollsCount(): number {
    return this.selectedRollNodes.filter((node) => node.data.type === 'roll').length;
  }

  getSelectedRollsWeight(): number {
    return this.selectedRollNodes.filter((node) => node.data.type === 'roll').reduce((sum, node) => sum + (node.data.finalWeight ?? 0), 0);
  }

  getSelectedRollsYards(): number {
    return this.selectedRollNodes.filter((node) => node.data.type === 'roll').reduce((sum, node) => sum + (node.data.yards ?? 0), 0);
  }

  getFabricByCode(fabricCode: string): Fabric | null {
    const selectedLine = this.selectedLine();
    if (!selectedLine?.workOrder) return null;
    return selectedLine.workOrder.fabrics?.find((f) => f.fabricCode === fabricCode)?.fabric || null;
  }

  getFabricDescription(fabricCode: string): string {
    const selectedLine = this.selectedLine();
    if (selectedLine?.workOrder) {
      const fabric = selectedLine.workOrder.fabrics?.find((f) => f.fabricCode === fabricCode)?.fabric;
      if (fabric?.longDescription) {
        return fabric.longDescription;
      }
    }

    for (const line of this.lines()) {
      if (line.workOrder) {
        const fabric = line.workOrder.fabrics?.find((f) => f.fabricCode === fabricCode)?.fabric;
        if (fabric?.longDescription) {
          return fabric.longDescription;
        }
      }
    }

    return fabricCode;
  }

  getColorName(colorId: string): string {
    return this.dispatchInstructionService.getColorName(colorId) ?? colorId;
  }

  getColorApprovedRecipeNumber(colorId: string): string | null {
    return this.dispatchInstructionService.getColorApprovedRecipeNumber(colorId);
  }

  onPriorityDateSelect(): void {
    const selectedDate = this.priorityDate.value;
    if (!selectedDate) return;

    this.loadInstructionsForDate(selectedDate);
  }

  onOpenPriorityDialog(): void {
    const di = this.dispatchInstruction();
    if (!di?.dateAndPriorityKey?.date) return;

    // Parse the date string (format: YYYY-MM-DD)
    const dateParts = di.dateAndPriorityKey.date.split('-');
    const existingDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));

    this.priorityDate.setValue(existingDate);
    this.loadInstructionsForDate(existingDate);
  }

  private loadInstructionsForDate(date: Date): void {
    const dateString = this.formatDateForApi(date);
    this.isPriorityLoading.set(true);

    this.dispatchInstructionProxyService
      .search({ priorityDateFrom: dateString, priorityDateTo: dateString })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isPriorityLoading.set(false)),
      )
      .subscribe({
        next: (response) => {
          const instructions = response.data?.items ?? [];
          instructions.sort((a, b) => (a.dateAndPriorityKey?.priority ?? 999) - (b.dateAndPriorityKey?.priority ?? 999));
          this.priorityListForDate.set(instructions);

          const currentDi = this.dispatchInstruction();
          this.originalPriorityIndex.set(instructions.findIndex((i) => i.id === currentDi?.id));

          this.showPriorityDialog.set(true);
        },
        error: () => {
          this.priorityListForDate.set([]);
          this.originalPriorityIndex.set(-1);
        },
      });
  }

  private formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  hasPriorityChanged(): boolean {
    const list = this.priorityListForDate();
    const currentDi = this.dispatchInstruction();
    if (!currentDi) return false;

    const currentIndex = list.findIndex((i) => i.id === currentDi.id);
    return currentIndex !== this.originalPriorityIndex();
  }

  onMoveUp(currentIndex: number): void {
    if (currentIndex <= 0) return;

    const list = [...this.priorityListForDate()];
    [list[currentIndex - 1], list[currentIndex]] = [list[currentIndex], list[currentIndex - 1]];
    this.priorityListForDate.set(list);
  }

  onMoveDown(currentIndex: number): void {
    const list = [...this.priorityListForDate()];
    if (currentIndex >= list.length - 1) return;

    [list[currentIndex], list[currentIndex + 1]] = [list[currentIndex + 1], list[currentIndex]];
    this.priorityListForDate.set(list);
  }

  onSavePriorityOrder(): void {
    const list = this.priorityListForDate();
    const currentDi = this.dispatchInstruction();
    if (!currentDi) return;

    const newIndex = list.findIndex((i) => i.id === currentDi.id);
    if (newIndex === -1) return;

    const newPriority = newIndex + 1;
    const selectedDate = this.priorityDate.value;
    if (!selectedDate) return;

    const dateString = this.formatDateForApi(selectedDate);

    this.isPriorityLoading.set(true);
    this.dispatchInstructionProxyService
      .updateDateAndPriority(currentDi.id, dateString, newPriority)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isPriorityLoading.set(false)),
      )
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.dispatchInstructionService.refreshDispatchInstruction(response.data);
          }
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            this.translate('Dispatch_Msg_Priority_Updated', 'Priority updated successfully'),
          );
          this.showPriorityDialog.set(false);
        },
      });
  }

  onSetPriorityAndDate(): void {
    const di = this.dispatchInstruction();
    const selectedDate = this.priorityDate.value;
    if (!di || !selectedDate) {
      GlobalMessageUtil.warn(
        this.translate('Common_Warning', 'Warning'),
        this.translate('Dispatch_Msg_Select_Date', 'Please select a date first'),
      );
      return;
    }

    const dateString = this.formatDateForApi(selectedDate);
    const newPriority = this.priorityListForDate().length + 1;

    this.isPriorityLoading.set(true);
    this.dispatchInstructionProxyService
      .updateDateAndPriority(di.id, dateString, newPriority)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isPriorityLoading.set(false)),
      )
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.dispatchInstructionService.refreshDispatchInstruction(response.data);
          }
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            this.translate('Dispatch_Msg_Priority_Date_Set', 'Priority and date set successfully'),
          );
          this.loadInstructionsForDate(selectedDate);
        },
      });
  }

  onRemovePriorityAndDate(): void {
    const di = this.dispatchInstruction();
    if (!di) return;

    this.confirmationService.confirm({
      key: 'main',
      message: this.translate('Dispatch_Confirm_Remove_Priority', 'Are you sure you want to remove the priority and date?'),
      header: this.translate('Dispatch_Confirm_Remove_Header', 'Confirm Remove'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.isPriorityLoading.set(true);
        this.dispatchInstructionProxyService
          .deleteDateAndPriority(di.id)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => this.isPriorityLoading.set(false)),
          )
          .subscribe({
            next: (response) => {
              if (response.data) {
                this.dispatchInstructionService.refreshDispatchInstruction(response.data);
              }
              GlobalMessageUtil.success(
                this.translate('Common_Success', 'Success'),
                this.translate('Dispatch_Msg_Priority_Date_Removed', 'Priority and date removed'),
              );
              this.priorityDate.reset();
              this.priorityListForDate.set([]);
              this.showPriorityDialog.set(false);
            },
          });
      },
    });
  }

  closePriorityDialog(): void {
    this.showPriorityDialog.set(false);
  }

  isCurrentInstruction(instruction: DispatchInstruction): boolean {
    return instruction.id === this.dispatchInstruction()?.id;
  }

  downloadPackingListOnly(): void {
    const di = this.dispatchInstruction();
    if (!di) return;

    this.isDownloadingAll.set(true);
    this.dispatchInstructionProxyService
      .downloadPackingList(di.id, false)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isDownloadingAll.set(false)),
      )
      .subscribe({
        next: (blob) => {
          const fileName = `PackingList_${di.number}_${new Date().getTime()}.xlsx`;
          this.downloadBlob(blob, fileName);
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            this.translate('Dispatch_Msg_Documents_Downloaded', 'All documents downloaded successfully'),
          );
        },
      });
  }

  downloadAllDocuments(): void {
    const di = this.dispatchInstruction();
    if (!di) return;

    this.isDownloadingAll.set(true);
    this.dispatchInstructionProxyService
      .downloadAllDocuments(di.id, false) // false = final invoice (with invoice number)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isDownloadingAll.set(false)),
      )
      .subscribe({
        next: (blob) => {
          const fileName = `ExportDocuments_${di.number}_${new Date().getTime()}.zip`;
          this.downloadBlob(blob, fileName);
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            this.translate('Dispatch_Msg_Documents_Downloaded', 'All documents downloaded successfully'),
          );
          // Reload to get updated invoice number if it was assigned
          this.loadDispatchInstruction(di.id);
        },
      });
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private loadClientAddresses(): void {
    const di = this.dispatchInstruction();
    if (!di?.clientReference) {
      this.isShippingDataReady.set(true);
      return;
    }

    this.isShippingDataReady.set(false);

    this.businessProxyService
      .getById(di.clientReference)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.clientBaseOveragePercentage.set(response.data?.clientDetails?.overagePercentage ?? null);
        },
      });

    const shippingToRef = di.shippingToReference || di.clientReference;
    this.businessProxyService
      .getById(shippingToRef)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const client = response.data;
          this.selectedShippingToClient.set(client ?? null);
          this.shippingToClientInput = client ?? null;
          if (client?.addresses) {
            const shippingAddresses = client.addresses.filter((addr) => addr.addressTypes?.includes(AddressType.Shipping)) ?? [];
            this.existingClientAddresses.set(shippingAddresses);

            const currentAddress = shippingAddresses.find((addr) => addr.code?.toString() === di.shipToAddressCode);
            this.selectedShipToAddress.set(currentAddress ?? null);
          }
          this.isShippingDataReady.set(true);

          const representatives = client?.representatives ?? [];
          this.existingClientRepresentatives.set(representatives);
          const currentRepresentative = representatives.find((rep) => rep.id === di.shippingToRepresentativeReference);
          this.selectedShippingToRepresentative.set(currentRepresentative ?? null);
        },
        error: () => {
          this.existingClientAddresses.set([]);
          this.selectedShippingToClient.set(null);
          this.shippingToClientInput = null;
          this.isShippingDataReady.set(true);
          this.existingClientRepresentatives.set([]);
          this.selectedShippingToRepresentative.set(null);
        },
      });

    if (di.consigneeReference) {
      this.businessProxyService
        .getById(di.consigneeReference)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            const client = response.data;
            this.selectedConsigneeClient.set(client ?? null);
            this.consigneeClientInput = client ?? null;
            if (client?.addresses) {
              const shippingAddresses = client.addresses.filter((addr) => addr.addressTypes?.includes(AddressType.Shipping)) ?? [];
              this.consigneeClientAddresses.set(shippingAddresses);

              const currentAddress = shippingAddresses.find((addr) => addr.code?.toString() === di.consigneeAddressCode);
              this.selectedConsigneeAddress.set(currentAddress ?? null);
            }
          },
          error: () => {
            this.consigneeClientAddresses.set([]);
            this.selectedConsigneeClient.set(null);
            this.consigneeClientInput = null;
          },
        });
    }

    const billingToRef = di.billToReference || di.clientReference;
    this.businessProxyService
      .getById(billingToRef)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const client = response.data;
          this.selectedBillingToClient.set(client ?? null);
          this.billingToClientInput = client ?? null;
          if (client?.addresses) {
            const billingAddresses = client.addresses.filter((addr) => addr.addressTypes?.includes(AddressType.Billing)) ?? [];
            this.billingClientAddresses.set(billingAddresses);

            const currentAddress = billingAddresses.find((addr) => addr.code?.toString() === di.billToAddressCode);
            this.selectedBillToAddress.set(currentAddress ?? null);
          }
        },
        error: () => {
          this.billingClientAddresses.set([]);
          this.selectedBillingToClient.set(null);
          this.billingToClientInput = null;
        },
      });
  }

  onShippingToClientSelect(event: AutoCompleteSelectEvent): void {
    const client = event.value as Business;
    this.selectedShippingToClient.set(client);
    this.shippingToClientInput = client;
    if (client?.addresses) {
      const shippingAddresses = client.addresses.filter((addr) => addr.addressTypes?.includes(AddressType.Shipping)) ?? [];
      this.existingClientAddresses.set(shippingAddresses);
      this.selectedShipToAddress.set(null);
    } else {
      this.existingClientAddresses.set([]);
      this.selectedShipToAddress.set(null);
    }

    this.existingClientRepresentatives.set(client?.representatives ?? []);
    this.selectedShippingToRepresentative.set(null);
  }

  onShippingToClientClear(): void {
    const di = this.dispatchInstruction();
    if (!di?.clientReference) return;

    this.shippingToClientInput = null;
    this.selectedShippingToClient.set(null);

    this.businessProxyService
      .getById(di.clientReference)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const client = response.data;
          // Don't set the input, just load addresses from billing client
          if (client?.addresses) {
            const shippingAddresses = client.addresses.filter((addr) => addr.addressTypes?.includes(AddressType.Shipping)) ?? [];
            this.existingClientAddresses.set(shippingAddresses);
            this.selectedShipToAddress.set(null);
          }
          this.existingClientRepresentatives.set(client?.representatives ?? []);
          this.selectedShippingToRepresentative.set(null);
        },
      });
  }

  onShipToAddressChange(address: Address): void {
    this.selectedShipToAddress.set(address);
  }

  onShippingToRepresentativeChange(representative: Representative): void {
    this.selectedShippingToRepresentative.set(representative);
  }

  onConsigneeClientSelect(event: AutoCompleteSelectEvent): void {
    const client = event.value as Business;
    this.selectedConsigneeClient.set(client);
    this.consigneeClientInput = client;
    if (client?.addresses) {
      const shippingAddresses = client.addresses.filter((addr) => addr.addressTypes?.includes(AddressType.Shipping)) ?? [];
      this.consigneeClientAddresses.set(shippingAddresses);
      this.selectedConsigneeAddress.set(null);
    } else {
      this.consigneeClientAddresses.set([]);
      this.selectedConsigneeAddress.set(null);
    }
  }

  onConsigneeClientClear(): void {
    this.consigneeClientInput = null;
    this.selectedConsigneeClient.set(null);
    this.consigneeClientAddresses.set([]);
    this.selectedConsigneeAddress.set(null);
  }

  onConsigneeAddressChange(address: Address): void {
    this.selectedConsigneeAddress.set(address);
  }

  onSaveConsigneeInfo(): void {
    const di = this.dispatchInstruction();
    if (!di) return;

    const consigneeClient = this.selectedConsigneeClient();
    const consigneeAddress = this.selectedConsigneeAddress();

    const command: DispatchInstructionShippingCommand = {
      shippingToReference: di.shippingToReference,
      shipToAddressCode: di.shipToAddressCode,
      consigneeReference: consigneeClient?.id,
      consigneeAddressCode: consigneeAddress?.code?.toString(),
      voyageId: di.voyageId,
      booking: di.booking,
    };

    this.increaseLoading();
    this.dispatchInstructionProxyService
      .updateShippingInfo(di.id, command)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.decreaseLoading()),
      )
      .subscribe({
        next: (response) => {
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            this.translate('Dispatch_Msg_Consignee_Updated', 'Consignee information updated successfully'),
          );
          if (response.data) {
            this.dispatchInstructionService.refreshDispatchInstruction(response.data);
            this.hasConsignee.set(!!response.data.consigneeReference);
          }
        },
      });
  }

  onBillingToClientSelect(event: AutoCompleteSelectEvent): void {
    const client = event.value as Business;
    this.selectedBillingToClient.set(client);
    this.billingToClientInput = client;
    if (client?.addresses) {
      const billingAddresses = client.addresses.filter((addr) => addr.addressTypes?.includes(AddressType.Billing)) ?? [];
      this.billingClientAddresses.set(billingAddresses);
      this.selectedBillToAddress.set(null);
    } else {
      this.billingClientAddresses.set([]);
      this.selectedBillToAddress.set(null);
    }
  }

  onBillingToClientClear(): void {
    const di = this.dispatchInstruction();
    if (!di?.clientReference) return;

    this.billingToClientInput = null;
    this.selectedBillingToClient.set(null);

    this.businessProxyService
      .getById(di.clientReference)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const client = response.data;
          if (client?.addresses) {
            const billingAddresses = client.addresses.filter((addr) => addr.addressTypes?.includes(AddressType.Billing)) ?? [];
            this.billingClientAddresses.set(billingAddresses);
            this.selectedBillToAddress.set(null);
          }
        },
      });
  }

  onBillToAddressChange(address: Address): void {
    this.selectedBillToAddress.set(address);
  }

  onSaveBillingInfo(): void {
    const di = this.dispatchInstruction();
    if (!di) return;

    const billingToClient = this.selectedBillingToClient();
    const billToAddress = this.selectedBillToAddress();

    const command: DispatchInstructionBillingCommand = {
      billToReference: billingToClient?.id,
      billToAddressCode: billToAddress?.code?.toString(),
      invoiceComment: this.invoiceComment.value ?? undefined,
    };

    this.increaseLoading();
    this.dispatchInstructionProxyService
      .updateBillingInfo(di.id, command)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.decreaseLoading()),
      )
      .subscribe({
        next: (response) => {
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            this.translate('Dispatch_Msg_Billing_Updated', 'Billing information updated successfully'),
          );
          if (response.data) {
            this.dispatchInstructionService.refreshDispatchInstruction(response.data);
            this.invoiceComment.setValue(response.data.invoiceComment ?? null);
          }
          this.invoiceComment.markAsPristine();
        },
      });
  }

  onSaveExtraTolerance(): void {
    const di = this.dispatchInstruction();
    if (!di) return;

    const percentage = this.extraTolerancePercentage.value;
    const comment = this.extraToleranceComment.value?.trim() || null;

    if (percentage && percentage > 0 && !comment) {
      GlobalMessageUtil.error(
        this.translate('Common_Validation_Error', 'Validation error. Please check your input.'),
        this.translate('Dispatch_Tolerance_Comment_Required', 'Comment is required when setting extra tolerance above 0%'),
      );
      return;
    }

    const command: DispatchInstructionExtraToleranceCommand = {
      extraTolerancePercentage: percentage ?? 0,
      extraToleranceComment: comment ?? '',
    };

    this.increaseLoading();
    this.dispatchInstructionProxyService
      .updateExtraTolerance(di.id, command)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.decreaseLoading()),
      )
      .subscribe({
        next: (response) => {
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            this.translate('Dispatch_Msg_Extra_Tolerance_Updated', 'Extra tolerance updated successfully'),
          );
          if (response.data) {
            this.dispatchInstructionService.refreshDispatchInstruction(response.data);
            this.extraTolerancePercentage.setValue(response.data.extraTolerancePercentage ?? null);
            this.extraToleranceComment.setValue(response.data.extraToleranceComment ?? null);
          }
          this.extraTolerancePercentage.markAsPristine();
          this.extraToleranceComment.markAsPristine();
        },
      });
  }

  // Download all documents with pre-invoice flag (Commercial Invoice with watermark, no invoice number)
  downloadPreInvoiceDocuments(): void {
    const di = this.dispatchInstruction();
    if (!di) return;

    this.isDownloadingAll.set(true);
    this.dispatchInstructionProxyService
      .downloadAllDocuments(di.id, true)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isDownloadingAll.set(false)),
      )
      .subscribe({
        next: (blob) => {
          const fileName = `PreInvoiceDocuments_${di.number}_${new Date().getTime()}.zip`;
          this.downloadBlob(blob, fileName);
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            this.translate('Dispatch_Msg_Documents_Downloaded', 'All documents downloaded successfully'),
          );
        },
      });
  }

  downloadSingleDocument(type: DispatchDocumentType): void {
    const di = this.dispatchInstruction();
    if (!di) return;

    this.isDownloadingAll.set(true);

    let download$: Observable<Blob>;
    let fileName: string;
    const timestamp = new Date().getTime();

    switch (type) {
      case 'packing-list':
        download$ = this.dispatchInstructionProxyService.downloadPackingList(di.id, this.canViewPrices);
        fileName = `PackingList_${di.number}_${timestamp}.xlsx`;
        break;
      case 'commercial-invoice':
        download$ = this.dispatchInstructionProxyService.downloadCommercialInvoice(di.id, false);
        fileName = `CommercialInvoice_${di.number}_${timestamp}.pdf`;
        break;
      case 'commercial-invoice-pre':
        download$ = this.dispatchInstructionProxyService.downloadCommercialInvoice(di.id, true);
        fileName = `PreInvoice_${di.number}_${timestamp}.pdf`;
        break;
      case 'shipping-instructions':
        download$ = this.dispatchInstructionProxyService.downloadShippingInstructions(di.id);
        fileName = `ShippingInstructions_${di.number}_${timestamp}.xlsx`;
        break;
      case 'cafta':
        download$ = this.dispatchInstructionProxyService.downloadCaftaCertificate(di.id);
        fileName = `CAFTA_${di.number}_${timestamp}.xlsx`;
        break;
    }

    download$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isDownloadingAll.set(false)),
      )
      .subscribe({
        next: (blob) => {
          this.downloadBlob(blob, fileName);
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            this.translate('Dispatch_Msg_Documents_Downloaded', 'All documents downloaded successfully'),
          );
          if (type === 'commercial-invoice') {
            this.loadDispatchInstruction(di.id);
          }
        },
      });
  }

  private buildCommonDocumentMenuItems(): MenuItem[] {
    return [
      {
        label: this.translate('Dispatch_Doc_Packing_List', 'Packing List'),
        icon: 'pi pi-file-excel',
        command: () => this.downloadSingleDocument('packing-list'),
      },
      {
        label: this.translate('Dispatch_Doc_Shipping_Instructions', 'Shipping Instructions'),
        icon: 'pi pi-file-excel',
        command: () => this.downloadSingleDocument('shipping-instructions'),
      },
      {
        label: this.translate('Dispatch_Doc_Cafta_Certificate', 'CAFTA Certificate'),
        icon: 'pi pi-file-excel',
        command: () => this.downloadSingleDocument('cafta'),
      },
      ...this.buildAffdMenuItems(),
    ];
  }

  private buildAffdMenuItems(): MenuItem[] {
    const clientPOs = this.dispatchInstruction()?.clientPOs ?? [];
    return clientPOs.map((po) => ({
      label: `AFFD — ${po}`,
      icon: 'pi pi-file-word',
      command: () => this.downloadAffdDocument(po),
    }));
  }

  downloadAffdDocument(clientProductOrderNumber: string): void {
    const di = this.dispatchInstruction();
    if (!di) return;

    this.isDownloadingAll.set(true);
    this.dispatchInstructionProxyService
      .downloadAffd(di.id, clientProductOrderNumber)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isDownloadingAll.set(false)),
      )
      .subscribe({
        next: (blob) => {
          const fileName = `AFFD_${clientProductOrderNumber}_${di.number}_${new Date().getTime()}.docx`;
          this.downloadBlob(blob, fileName);
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            this.translate('Dispatch_Msg_Documents_Downloaded', 'All documents downloaded successfully'),
          );
        },
      });
  }

  // UI-only state for the customizations dialog
  expandedCustomizations = new Set<string>();
  customizationRollTrees = new Map<string, ScannedRollTreeNode[]>();
  selectedCustomizationRolls = new Map<string, ScannedRollTreeNode[]>();
  splitUnitPrices = new Map<string, number | null>();

  // Cross-Production-Card quick assign: lets the user filter/select rolls across several
  // per-PC customizations at once and assign them all the same price in a single action.
  // Purely a capture-time convenience — on "Add Split" the rolls are distributed back into
  // each roll's own (FabricCode|Color|ProductionCard) customization as a regular split, so the
  // stored model (and the invoice, which already groups by Fabric+Color+price) is unchanged.
  crossPcFilterFabricCode = signal<string>('');
  crossPcFilterFabricName = signal<string>('');
  crossPcFilterColor = signal<string>('');
  crossPcFilterProductionCard = signal<string>('');
  crossPcSelectedRows: CrossPcRollRow[] = [];
  crossPcUnitPrice: number | null = null;

  onOpenFabricCustomizationsDialog(): void {
    const di = this.dispatchInstruction();
    if (!di) return;

    const keySet = new Map<string, Pick<FabricCustomization, 'fabricCode' | 'colorReference' | 'productionCardNumber'>>();
    const rollsByKey = new Map<string, CustomizationRollEntry[]>();

    for (const line of di.lines) {
      for (const fab of line.fabricsAndBalances) {
        for (const sr of fab.scannedRolls) {
          const pcRef = sr.roll?.productionCardReference ?? '';
          const pc = line.productionCards?.find((p) => p.id === pcRef);
          const pcNumber = pc?.productionCardNumber ?? '';
          const colorRef = sr.roll?.colorReference ?? line.colorReference ?? '';
          const key = `${fab.fabricCode}|${colorRef}|${pcNumber}`;

          if (!keySet.has(key)) {
            keySet.set(key, { fabricCode: fab.fabricCode, colorReference: colorRef, productionCardNumber: pcNumber });
            rollsByKey.set(key, []);
          }
          if (sr.roll) {
            rollsByKey.get(key)!.push({ roll: sr.roll, rollNumber: sr.rollNumber, scannedRoll: sr });
          }
        }
      }
    }

    const existingCustomizations = di.fabricCustomizations ?? [];
    const customizations: FabricCustomization[] = Array.from(keySet.entries()).map(([, entry]) => {
      const existing = existingCustomizations.find(
        (c) =>
          c.fabricCode === entry.fabricCode &&
          (c.colorReference ?? '') === entry.colorReference &&
          (c.productionCardNumber ?? '') === entry.productionCardNumber,
      );

      const splits: FabricCustomizationSplit[] =
        existing?.splits && existing.splits.length > 0
          ? existing.splits.map((s) => ({ rollNumbers: [...s.rollNumbers], unitPrice: s.unitPrice }))
          : [];

      return {
        fabricCode: entry.fabricCode,
        colorReference: entry.colorReference,
        productionCardNumber: entry.productionCardNumber,
        aliasReference: existing?.aliasReference,
        splits,
      };
    });

    this.customizationRollTrees.clear();
    this.selectedCustomizationRolls.clear();
    this.splitUnitPrices.clear();

    for (const [key] of keySet) {
      const rolls = rollsByKey.get(key) ?? [];

      const rollNodes: ScannedRollTreeNode[] = rolls.map((r) => ({
        data: {
          type: 'roll' as const,
          rollNumber: r.rollNumber,
          colorReference: r.roll.colorReference,
          weightInLbs: r.roll.finalWeight?.weightInLbs,
          weightInKg: this.lbsToKg(r.roll.finalWeight?.weightInLbs ?? 0),
          yards: r.roll.finalLengthInYards ?? 0,
          scannedRoll: r.scannedRoll,
        },
      }));

      this.customizationRollTrees.set(key, rollNodes);
      this.selectedCustomizationRolls.set(key, []);
      this.splitUnitPrices.set(key, null);
    }

    this.expandedCustomizations.clear();
    this.editableFabricCustomizations.set(customizations);

    // Reset cross-PC quick assign state (filters + selection may reference stale objects otherwise)
    this.crossPcFilterFabricCode.set('');
    this.crossPcFilterFabricName.set('');
    this.crossPcFilterColor.set('');
    this.crossPcFilterProductionCard.set('');
    this.crossPcSelectedRows = [];
    this.crossPcUnitPrice = null;

    this.showFabricCustomizationsDialog.set(true);
  }

  onCloseFabricCustomizationsDialog(): void {
    this.showFabricCustomizationsDialog.set(false);
  }

  onSaveFabricCustomizations(): void {
    const di = this.dispatchInstruction();
    if (!di) return;

    const customizations = this.editableFabricCustomizations().map((c) => ({
      fabricCode: c.fabricCode,
      colorReference: c.colorReference,
      productionCardNumber: c.productionCardNumber,
      aliasReference: c.aliasReference,
      splits: c.splits
        .filter((s) => s.rollNumbers.length > 0)
        .map((s) => ({
          rollNumbers: s.rollNumbers,
          unitPrice: s.unitPrice,
        })),
    }));

    this.increaseLoading();
    this.dispatchInstructionProxyService
      .updateFabricCustomizations(di.id, customizations)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.decreaseLoading()),
      )
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.dispatchInstructionService.refreshDispatchInstruction(response.data);
          }
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            this.translate('Dispatch_Msg_Customizations_Saved', 'Fabric customizations saved successfully'),
          );
          this.showFabricCustomizationsDialog.set(false);
        },
      });
  }

  // --- Fabric Customization Split helpers ---

  getCustomizationKey(c: FabricCustomization): string {
    return `${c.fabricCode}|${c.colorReference ?? ''}|${c.productionCardNumber ?? ''}`;
  }

  isCustomizationExpanded(c: FabricCustomization): boolean {
    return this.expandedCustomizations.has(this.getCustomizationKey(c));
  }

  toggleCustomizationExpand(c: FabricCustomization): void {
    const key = this.getCustomizationKey(c);
    if (this.expandedCustomizations.has(key)) {
      this.expandedCustomizations.delete(key);
    } else {
      this.expandedCustomizations.add(key);
    }
  }

  getAvailableRollsForCustomization(c: FabricCustomization): ScannedRollTreeNode[] {
    const key = this.getCustomizationKey(c);
    const allRolls = this.customizationRollTrees.get(key) ?? [];
    const assignedRollNumbers = new Set(c.splits.flatMap((s) => s.rollNumbers));
    return allRolls.filter((r) => !assignedRollNumbers.has(r.data.rollNumber ?? ''));
  }

  getSelectedCustomizationRolls(c: FabricCustomization): ScannedRollTreeNode[] {
    return this.selectedCustomizationRolls.get(this.getCustomizationKey(c)) ?? [];
  }

  onCustomizationRollSelect(c: FabricCustomization, selection: ScannedRollTreeNode[]): void {
    this.selectedCustomizationRolls.set(this.getCustomizationKey(c), selection);
  }

  getSelectedCustomizationRollsCount(c: FabricCustomization): number {
    return this.getSelectedCustomizationRolls(c).filter((n) => n.data.type === 'roll').length;
  }

  getSelectedCustomizationRollsWeight(c: FabricCustomization): number {
    return this.getSelectedCustomizationRolls(c)
      .filter((n) => n.data.type === 'roll')
      .reduce((sum, n) => sum + (n.data.weightInLbs ?? 0), 0);
  }

  getSplitUnitPrice(c: FabricCustomization): number | null {
    return this.splitUnitPrices.get(this.getCustomizationKey(c)) ?? null;
  }

  setSplitUnitPrice(c: FabricCustomization, value: number | null): void {
    this.splitUnitPrices.set(this.getCustomizationKey(c), value);
  }

  addSplitFromSelection(c: FabricCustomization): void {
    const key = this.getCustomizationKey(c);
    const selected = this.getSelectedCustomizationRolls(c).filter((n) => n.data.type === 'roll');
    if (selected.length === 0) return;

    const rollNumbers = selected.map((n) => n.data.rollNumber!);
    const unitPrice = this.splitUnitPrices.get(key) ?? undefined;

    c.splits.push({ rollNumbers, unitPrice });

    this.selectedCustomizationRolls.set(key, []);
    this.splitUnitPrices.set(key, null);

    // New array reference so the signal notifies.
    this.editableFabricCustomizations.set([...this.editableFabricCustomizations()]);
  }

  removeSplit(c: FabricCustomization, index: number): void {
    c.splits.splice(index, 1);
    this.editableFabricCustomizations.set([...this.editableFabricCustomizations()]);
  }

  getSplitRollsInfo(c: FabricCustomization, split: FabricCustomizationSplit): SplitRollsInfo {
    const key = this.getCustomizationKey(c);
    const allRolls = this.customizationRollTrees.get(key) ?? [];
    const splitRolls = allRolls.filter((r) => split.rollNumbers.includes(r.data.rollNumber ?? ''));
    return {
      count: splitRolls.length,
      lbs: splitRolls.reduce((sum, r) => sum + (r.data.weightInLbs ?? 0), 0),
      yds: splitRolls.reduce((sum, r) => sum + (r.data.yards ?? 0), 0),
    };
  }

  getWorkOrderUomForCustomization(c: FabricCustomization): UnitOfMeasure {
    const di = this.dispatchInstruction();
    if (!di) return UnitOfMeasure.lb;
    for (const line of di.lines) {
      if ((line.colorReference ?? '') === (c.colorReference ?? '')) {
        return line.workOrder?.uom ?? UnitOfMeasure.lb;
      }
    }
    return UnitOfMeasure.lb;
  }

  getSplitTotal(c: FabricCustomization, split: FabricCustomizationSplit): number {
    const info = this.getSplitRollsInfo(c, split);
    const uom = this.getWorkOrderUomForCustomization(c);
    const qty = uom === UnitOfMeasure.yd ? info.yds : info.lbs;
    return Math.round(qty * (split.unitPrice ?? 0) * 100) / 100;
  }

  /** All rolls of the customization, not only the ones already in a split. */
  getCustomizationTotalLbs(c: FabricCustomization): number {
    const key = this.getCustomizationKey(c);
    const allRolls = this.customizationRollTrees.get(key) ?? [];
    return allRolls.reduce((sum, r) => sum + (r.data.weightInLbs ?? 0), 0);
  }

  getCustomizationTotalYds(c: FabricCustomization): number {
    const key = this.getCustomizationKey(c);
    const allRolls = this.customizationRollTrees.get(key) ?? [];
    return allRolls.reduce((sum, r) => sum + (r.data.yards ?? 0), 0);
  }

  getCustomizationRollCount(c: FabricCustomization): number {
    const key = this.getCustomizationKey(c);
    return (this.customizationRollTrees.get(key) ?? []).length;
  }

  // --- Cross-Production-Card quick assign ---

  /**
   * Unassigned rolls across ALL production cards, filtered by color/fabric code/fabric name/PC.
   * MUST be a computed (memoized, stable array reference) rather than a plain method: a plain
   * method bound to [value] on a p-table returns a brand-new array every change-detection cycle,
   * which combined with a two-way [(selection)] binding can make PrimeNG's table re-run its
   * internal selection/value reconciliation every cycle and never settle (observed as the whole
   * dialog freezing on open, with zero network activity since none of this is server-driven).
   */
  readonly filteredCrossPcRolls = computed<CrossPcRollRow[]>(() => {
    const fabricCode = this.crossPcFilterFabricCode().trim().toLowerCase();
    const fabricName = this.crossPcFilterFabricName().trim().toLowerCase();
    const color = this.crossPcFilterColor().trim().toLowerCase();
    const productionCard = this.crossPcFilterProductionCard().trim().toLowerCase();

    const rows: CrossPcRollRow[] = [];
    for (const c of this.editableFabricCustomizations()) {
      const key = this.getCustomizationKey(c);
      for (const node of this.getAvailableRollsForCustomization(c)) {
        rows.push({
          rollNumber: node.data.rollNumber ?? '',
          fabricCode: c.fabricCode,
          colorReference: c.colorReference ?? '',
          productionCardNumber: c.productionCardNumber ?? '',
          weightInLbs: node.data.weightInLbs ?? 0,
          yards: node.data.yards ?? 0,
          customizationKey: key,
        });
      }
    }

    return rows.filter((row) => {
      if (fabricCode && !row.fabricCode.toLowerCase().includes(fabricCode)) return false;
      if (fabricName && !this.getFabricDescription(row.fabricCode).toLowerCase().includes(fabricName)) return false;
      if (color && !this.getColorName(row.colorReference).toLowerCase().includes(color)) return false;
      if (productionCard && !row.productionCardNumber.toLowerCase().includes(productionCard)) return false;
      return true;
    });
  });

  /** One-way selection handler for the cross-PC table (avoids two-way binding feedback loops) */
  onCrossPcSelectionChange(selection: CrossPcRollRow[]): void {
    this.crossPcSelectedRows = selection ?? [];
  }

  /**
   * Adds one split per production card represented in the current cross-PC selection, all with the
   * same unit price. Each split lands in that roll's own (FabricCode|Color|ProductionCard)
   * customization — the model on disk stays exactly as it is today; this only saves the user from
   * having to repeat "select rolls + type price + Add Split" once per production card.
   */
  addCrossPcSplit(): void {
    if (this.crossPcSelectedRows.length === 0) return;

    const unitPrice = this.crossPcUnitPrice ?? undefined;
    const rollNumbersByCustomizationKey = new Map<string, string[]>();
    for (const row of this.crossPcSelectedRows) {
      const rollNumbers = rollNumbersByCustomizationKey.get(row.customizationKey) ?? [];
      rollNumbers.push(row.rollNumber);
      rollNumbersByCustomizationKey.set(row.customizationKey, rollNumbers);
    }

    const customizations = this.editableFabricCustomizations();
    for (const [key, rollNumbers] of rollNumbersByCustomizationKey) {
      const customization = customizations.find((c) => this.getCustomizationKey(c) === key);
      if (customization) {
        customization.splits.push({ rollNumbers, unitPrice });
      }
    }

    this.crossPcSelectedRows = [];
    this.crossPcUnitPrice = null;
    this.editableFabricCustomizations.set([...customizations]);
  }

  // #region Chemical dispatch actions (Pride Chemicals)

  public fetchChemicalClientPOs = (query: string): Observable<ClientPurchaseOrder[]> => {
    const clientReference = this.dispatchInstruction()?.clientReference;
    if (!clientReference) return of([]);

    return this.clientPurchaseOrderProxyService
      .search(
        {
          number: query,
          clientReference,
          status: [ClientPurchaseOrderStatus.Approved],
        },
        1,
        25,
      )
      .pipe(map((response) => response.data?.items ?? []));
  };

  onChemicalClientPOSelected(po: ClientPurchaseOrder | null): void {
    this.selectedChemicalClientPO.set(po);
    this.chemicalForm.controls.itemCode.reset(null);
    this.chemicalEntryItemCode.set(null);
    if (po) this.loadChemicalPoReferenceData(po);
  }

  onSelectChemicalEntryItem(item: ClientPurchaseOrderItem): void {
    this.chemicalForm.controls.itemCode.setValue(item.itemCode);
    this.chemicalEntryItemCode.set(item.itemCode);
    if (this.availableChemicalLotItems().length === 0) {
      this.loadAvailableChemicalLotItems();
    }
  }

  onSelectChemicalLine(line: ChemicalDispatchInstructionLine): void {
    this.selectedChemicalLineId.set(line.lineId);
    this.loadChemicalLotItems();
  }

  private loadChemicalLotItems(): void {
    this.loadAvailableChemicalLotItems();
    this.loadAssignedChemicalLotItems();
  }

  private loadAvailableChemicalLotItems(): void {
    const clientReference = this.dispatchInstruction()?.clientReference;
    if (!clientReference) return;
    this.dispatchInstructionProxyService
      .getAvailableChemicalLotItems(clientReference, this.dispatchInstruction()?.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const lots = response.data ?? [];
          this.availableChemicalLotItems.set(lots);
          this.resolveChemicalBatchNumbers(lots, this.availableChemicalLotItems);
        },
      });
  }

  private loadAssignedChemicalLotItems(): void {
    const lotItemNumbers = Array.from(this.assignedChemicalLotItemNumbers());
    if (lotItemNumbers.length === 0) {
      this.assignedChemicalLotItems.set([]);
      return;
    }
    this.lotItemProxyService
      .getByLotNumbers(lotItemNumbers)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const lots = response.data ?? [];
          this.assignedChemicalLotItems.set(lots);
          this.resolveChemicalBatchNumbers(lots, this.assignedChemicalLotItems);
        },
      });
  }

  private resolveChemicalBatchNumbers(lots: LotItem[], target: WritableSignal<LotItem[]>): void {
    const references = Array.from(new Set(lots.filter((l) => l.batchReference).map((l) => l.batchReference as string)));
    if (references.length === 0) return;

    this.batchProxyService
      .getByIds(references)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const numberByReference = new Map<string, string>();
          for (const batch of response.data ?? []) {
            if (batch.id && batch.number) numberByReference.set(batch.id, batch.number);
          }
          target.update((items) =>
            items.map((item) => ({
              ...item,
              batchNumber: item.batchReference ? numberByReference.get(item.batchReference) : undefined,
            })),
          );
        },
      });
  }

  private loadChemicalItemMasters(): void {
    const itemCodes = (this.dispatchInstruction()?.chemicalLines ?? []).map((l) => l.itemCode).filter(Boolean);
    this.mergeChemicalItemMasters(itemCodes);
  }

  private mergeChemicalItemMasters(itemCodes: string[]): void {
    const cached = this.chemicalItemMastersByCode();
    const missing = Array.from(new Set(itemCodes.filter((code) => code && !cached.has(code))));
    if (missing.length === 0) return;
    this.itemMasterProxyService
      .getByItemCodes(missing)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.chemicalItemMastersByCode.update((map) => {
            const next = new Map(map);
            for (const item of response.data ?? []) {
              if (item.itemCode) next.set(item.itemCode, item);
            }
            return next;
          });
        },
      });
  }

  private loadChemicalPoReferenceData(po: ClientPurchaseOrder): void {
    this.mergeChemicalItemMasters(po.items.map((i) => i.itemCode));
    const recipeNumbers = po.items.map((i) => i.productRecipeNumber).filter((n): n is string => !!n);
    this.loadChemicalRecipeNames(recipeNumbers);
  }

  private loadChemicalRecipeNames(recipeNumbers: string[]): void {
    const cached = this.chemicalRecipeNamesByNumber();
    const missing = Array.from(new Set(recipeNumbers.filter((number) => number && !cached.has(number))));
    if (missing.length === 0) return;
    this.productRecipeProxyService
      .getByNumbers(missing)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.chemicalRecipeNamesByNumber.update((map) => {
            const next = new Map(map);
            for (const recipe of response.data ?? []) {
              if (recipe.number) next.set(recipe.number, recipe.name);
            }
            return next;
          });
        },
      });
  }

  /** Adds a lot item directly from the Chemical Entry preview table (creates the line if needed). */
  onAddChemicalLotItemFromEntry(lotItem: LotItem): void {
    const di = this.dispatchInstruction();
    const po = this.chemicalForm.controls.clientPurchaseOrder.value;
    const itemCode = this.chemicalForm.controls.itemCode.value;
    if (!di?.id || !itemCode || !lotItem.number) return;

    const command: AddChemicalLotItemCommand = {
      itemCode,
      clientPurchaseOrderReference: po?.id,
      clientPurchaseOrderNumber: po?.number,
      lotItemNumber: lotItem.number,
    };

    this.increaseLoading();
    this.dispatchInstructionProxyService
      .addChemicalLotItem(di.id, command)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.decreaseLoading()),
      )
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.dispatchInstructionService.refreshDispatchInstruction(response.data);
            const line = response.data.chemicalLines.find(
              (l) => l.itemCode === itemCode && (l.clientPurchaseOrderReference ?? null) === (po?.id ?? null),
            );
            if (line) this.selectedChemicalLineId.set(line.lineId);
          }
          this.loadChemicalLotItems();
          this.loadChemicalItemMasters();
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            this.translate('Dispatch_Msg_Lot_Item_Added', 'Lot item added'),
          );
        },
      });
  }

  onAddChemicalLotItem(lotItem: LotItem): void {
    const di = this.dispatchInstruction();
    const line = this.selectedChemicalLine();
    if (!di?.id || !line || !lotItem.number) return;

    const command: AddChemicalLotItemCommand = {
      itemCode: line.itemCode,
      clientPurchaseOrderReference: line.clientPurchaseOrderReference,
      clientPurchaseOrderNumber: line.clientPurchaseOrderNumber,
      lotItemNumber: lotItem.number,
    };

    this.increaseLoading();
    this.dispatchInstructionProxyService
      .addChemicalLotItem(di.id, command)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.decreaseLoading()),
      )
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.dispatchInstructionService.refreshDispatchInstruction(response.data);
          }
          this.loadChemicalLotItems();
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            this.translate('Dispatch_Msg_Lot_Item_Added', 'Lot item added'),
          );
        },
      });
  }

  onRemoveChemicalLine(line: ChemicalDispatchInstructionLine): void {
    const di = this.dispatchInstruction();
    if (!di?.id) return;

    this.confirmationService.confirm({
      key: 'main',
      message: this.translate(
        'Dispatch_Confirm_Remove_Chemical_Line',
        'Are you sure you want to delete this chemical line and all its assigned lot items?',
      ),
      accept: () => {
        this.increaseLoading();
        this.dispatchInstructionProxyService
          .removeChemicalLine(di.id, line.lineId)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => this.decreaseLoading()),
          )
          .subscribe({
            next: (response) => {
              if (response.data) {
                this.dispatchInstructionService.refreshDispatchInstruction(response.data);
              }
              if (this.selectedChemicalLineId() === line.lineId) {
                this.selectedChemicalLineId.set(null);
              }
              this.loadChemicalLotItems();
              GlobalMessageUtil.success(
                this.translate('Common_Success', 'Success'),
                this.translate('Dispatch_Msg_Chemical_Line_Removed', 'Chemical line removed'),
              );
            },
          });
      },
    });
  }

  onRemoveChemicalLotItem(line: ChemicalDispatchInstructionLine, lotItemNumber: string): void {
    const di = this.dispatchInstruction();
    if (!di?.id) return;

    this.confirmationService.confirm({
      key: 'main',
      message: this.translate('Dispatch_Confirm_Remove_Lot_Item', 'Are you sure you want to remove this lot item from the dispatch?'),
      accept: () => {
        this.increaseLoading();
        this.dispatchInstructionProxyService
          .removeChemicalLotItem(di.id, line.lineId, lotItemNumber)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => this.decreaseLoading()),
          )
          .subscribe({
            next: (response) => {
              if (response.data) {
                this.dispatchInstructionService.refreshDispatchInstruction(response.data);
                const stillExists = response.data.chemicalLines.some((l) => l.lineId === line.lineId);
                if (!stillExists) {
                  this.selectedChemicalLineId.set(null);
                }
              }
              this.loadChemicalLotItems();
              GlobalMessageUtil.success(
                this.translate('Common_Success', 'Success'),
                this.translate('Dispatch_Msg_Lot_Item_Removed', 'Lot item removed'),
              );
            },
          });
      },
    });
  }

  /** Groups lot items by their resolved batch number; lots without a batch land in a trailing null group. */
  private groupLotItemsByBatch(items: LotItem[]): LotItemBatchGroup[] {
    const map = new Map<string | null, LotItem[]>();
    for (const item of items) {
      const key = item.batchNumber ?? null;
      const bucket = map.get(key) ?? [];
      bucket.push(item);
      map.set(key, bucket);
    }
    return Array.from(map.entries())
      .map(([batchNumber, groupItems]) => ({ batchNumber, items: groupItems }))
      .sort((a, b) => {
        if (a.batchNumber === null) return 1;
        if (b.batchNumber === null) return -1;
        return a.batchNumber.localeCompare(b.batchNumber);
      });
  }

  getChemicalItemName(itemCode: string): string {
    const item = this.chemicalItemMastersByCode().get(itemCode);
    return item?.itemDisplayName?.en ?? item?.itemDisplayName?.es ?? '';
  }

  getChemicalRecipeNumber(itemCode: string): string | null {
    return this.chemicalItemMastersByCode().get(itemCode)?.baseProductRecipeNumber ?? null;
  }

  getChemicalRecipeName(recipeNumber: string | null | undefined): string {
    return recipeNumber ? (this.chemicalRecipeNamesByNumber().get(recipeNumber) ?? '') : '';
  }

  getChemicalItemUom(itemCode: string | null | undefined): string {
    if (!itemCode) return '';
    const uom = this.chemicalItemMastersByCode().get(itemCode)?.unitOfMeasure;
    return uom != null ? (this.unitOfMeasures[uom] ?? '') : '';
  }

  // #endregion

  onNoteAdded(): void {
    const di = this.dispatchInstruction();
    if (!di) return;

    // The csl-standard-notes component already adds the note to the notes array internally.
    // Just use the current notes as-is to avoid duplication.
    const currentNotes = di.notes || [];

    this.increaseLoading();
    this.dispatchInstructionProxyService
      .updateNotes(di.id, currentNotes)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.decreaseLoading()),
      )
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.dispatchInstructionService.refreshDispatchInstruction(response.data);
          }
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            this.translate('Dispatch_Msg_Note_Added', 'Note added successfully'),
          );
        },
      });
  }
}
